import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TopBar from '../../../layouts/TopBar';
import '../../../shared/styles/Page.css';
import '../../../shared/styles/Orchestrator.css';
import '../../../shared/styles/Toastify.css';
import { Button } from '../../../shared/components/Button';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import BreakScreen from '../../../shared/components/BreakScreen';
import PreparingScreen from '../../../shared/components/PreparingScreen';
import ManagerSidebar from './ManagerSidebar';
import GridBoard from './GridBoard';
import ViewportIndicator from './ViewportIndicator';
import ZoomRepeatButton from './ZoomRepeatButton';
import SceneModal from './modals/SceneModal';
import DeleteSceneModal from './modals/DeleteSceneModal';
import TokenModal from './modals/TokenModal';
import LayerModal from './modals/LayerModal';
import EffectModal from './modals/EffectModal';
import BattleEffectModal from './modals/BattleEffectModal';
import DiceHistoryModal from './modals/DiceHistoryModal';
import GmDiceModal from './modals/GmDiceModal';
import SystemSelectModal from './modals/SystemSelectModal';
import SystemDmScreenModal from './modals/SystemDmScreenModal';
import TrackerModal from './modals/TrackerModal';
import GridConfigModal from './modals/GridConfigModal';
import TrackerToastContent from './TrackerToastContent';
import LoadTokensModal from './modals/LoadTokensModal';
import WelcomeTutorial from './WelcomeTutorial';
import EditScenesModal from './modals/EditScenesModal';
import WorldMapModal from './modals/WorldMapModal';
import LibraryModal from './modals/LibraryModal';
import AssetManagerModal from './modals/AssetManagerModal';
import CloudAccessModal from './modals/CloudAccessModal';
import GameplaySyncErrorModal from './modals/GameplaySyncErrorModal';

import { migrateSceneImages, storeImage, getImage } from '../utils/storage';
import { compareImageFileNames, resizeImage } from '../utils/imageUtils';
import { getAssetUrl, setAssetUrl, uploadToStorage } from '../utils/assetUpload';
import {
  loadScenes,
  saveScenes,
  getStoredSceneId,
  setStoredSceneId,
} from '../utils/sceneStorage';
import {
  DEFAULT_GRID_COLS,
  DEFAULT_GRID_ROWS,
  DEFAULT_GRID_TYPE,
  normalizeGridConfig,
  getTokenGridPositionFromWorldPoint,
  hexCellToPercent,
  hexGridAspectRatio,
} from '../utils/gridConfig';
import {
  DEFAULT_GRID_VIEW_STATE,
  loadGridViewState,
  normalizeGridViewState,
  saveGridViewState,
} from '../utils/gridViewState';
import { createGridViewportStore, getGridViewportMetrics } from '../utils/gridViewport';
import {
  clampGridZoom,
  getAdjacentGridZoomPreset,
  GRID_ZOOM_STEP,
} from '../utils/gridZoom';
import { applyBattleEffectCellChanges } from '../utils/battleEffectCells';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db } from '../../../firebase/firebaseApp';
import {
  createSession,
  listenSessionCommands,
  listenSessionRolls,
  markCommandProcessed,
  updateSessionDoc,
  updateSessionSnapshot,
  writeSessionRoll,
} from '../utils/sessions';
import {
  getGameplaySyncEnabled,
  setGameplaySyncEnabledLocal,
  saveSceneGameplay,
  saveAllScenesGameplay,
  deleteSceneGameplay,
  loadScenesGameplay,
} from '../utils/gameplaySync';
import { loadWorldMap, saveWorldMap, buildPlayerWorldMap } from '../utils/worldMap';
import { loadLibrary, saveLibrary, buildPlayerLibrary } from '../utils/library';
import { fetchUserTier } from '../../auth/utils/provisioning';
import { parseDiceCommand, resolveRoll } from '../utils/diceCommands';
import { getUserTheme } from '../../../shared/utils/userTheme';
import {
  getInitialTrackers,
  saveTrackers,
  FEAR_TRACKER_ID,
  buildFearTracker,
} from '../utils/trackerStorage';
import {
  PRESENTING_KEY,
  PRESENTING_SIZE,
  GRID_COLS,
  GRID_ROWS,
  clampEffectOpacity,
  VALID_EFFECT_SHAPES,
  clampEffectSize,
  generateShapeCells,
  buildBattleEffectDefaults,
  getGridUnitsForSize,
  getStoredSizeValue,
  getDaggerheartTokenFields,
  buildPlayerScenePayload,
  nudgeTokenOnScene,
  placeTokensAtBottomRight,
  getDuplicateTokenNames,
  GM_THEME,
  TRACKER_ANNOUNCE_ID,
  TRACKER_ANNOUNCE_STYLE,
  buildTrackerHistoryRecord,
  processScenePayload,
} from '../utils/orchestratorManagerHelpers';

/**
 * Build a richer scene payload for players when gameplay sync is enabled.
 * Includes layer background URLs, token image URLs, battle effects, and scene effect.
 * Used when the DM has gameplay sync enabled so players can render the full GridBoard.
 */
const buildFullPlayerScenePayload = (scene) => {
  if (!scene) return null;

  const layers = (scene.layers || []).map((layer) => {
    const imageUrl = layer.dataUrl || getAssetUrl(layer.imageId);
    return {
      id: layer.id,
      visible: layer.visible !== false,
      ...(imageUrl ? { imageUrl } : {}),
    };
  });

  const tokens = (scene.tokens || [])
    .filter((token) => token && token.visible !== false)
    .map((token) => {
      const goodUrl = getAssetUrl(token.goodImageId || token.imageId);
      const evilUrl = getAssetUrl(token.evilImageId || token.altImageId);
      const neutralUrl = getAssetUrl(token.neutralImageId);
      return {
        id: token.id,
        name: token.name || '',
        x: Number(token.x) || 0,
        y: Number(token.y) || 0,
        size: token.size,
        disposition: token.disposition || null,
        showInitial: token.showInitial !== false,
        grayscale: !!token.grayscale,
        conditions: Array.isArray(token.conditions) ? token.conditions : [],
        ...getDaggerheartTokenFields(token),
        ...(goodUrl ? { goodImageUrl: goodUrl } : {}),
        ...(evilUrl ? { evilImageUrl: evilUrl } : {}),
        ...(neutralUrl ? { neutralImageUrl: neutralUrl } : {}),
      };
    });

  const battleEffects = (scene.battleEffects || [])
    .filter((effect) => effect && effect.visible !== false)
    .map((effect) => ({
      id: effect.id,
      kind: effect.kind || 'fog',
      name: effect.name || '',
      color: effect.color || null,
      stroke: effect.stroke || null,
      opacity: effect.opacity ?? 0.4,
      visible: true,
      cells: (effect.cells || []).map((cell) => ({ x: cell.x, y: cell.y })),
    }));

  return {
    id: scene.id || null,
    name: scene.name || '',
    ttrpgSystem: scene.ttrpgSystem || null,
    effect: scene.effect || null,
    gridConfig: normalizeGridConfig(scene.gridConfig),
    layers,
    tokens,
    battleEffects,
  };
};

function OrchestratorManager() {
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('appearance');
    if (!saved) return 'light';
    try {
      const parsed = JSON.parse(saved);
      return parsed?.mode === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const [scenes, setScenes] = useState(() => loadScenes());
  const [currentSceneId, setCurrentSceneId] = useState(() =>
    getStoredSceneId()
  );
  const [gmViewState, setGmViewState] = useState(() =>
    loadGridViewState(getStoredSceneId(), 'gm')
  );
  const [gmViewSceneId, setGmViewSceneId] = useState(() => getStoredSceneId());
  const displayViewportStoreRef = useRef(null);
  if (!displayViewportStoreRef.current) {
    displayViewportStoreRef.current = createGridViewportStore();
  }
  const displayViewportStore = displayViewportStoreRef.current;
  const displayViewportRef = useRef(null);
  const gmViewStateRef = useRef(gmViewState);
  const displayWindowRef = useRef(null);
  const [isDisplayOpen, setIsDisplayOpen] = useState(false);

  const syncDisplayWindowState = useCallback(() => {
    const displayWindow = displayWindowRef.current;
    setIsDisplayOpen(!!(displayWindow && !displayWindow.closed));
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(syncDisplayWindowState, 750);
    return () => window.clearInterval(intervalId);
  }, [syncDisplayWindowState]);

  const openOrRefreshDisplay = useCallback(() => {
    const displayWindow = displayWindowRef.current;
    if (displayWindow && !displayWindow.closed) {
      try {
        displayWindow.location.reload();
        displayWindow.focus();
      } catch {
        try {
          displayWindow.focus();
        } catch {}
      }
      syncDisplayWindowState();
      return;
    }

    displayWindowRef.current = window.open(
      '/orchestrator/display',
      'displayWindow',
      'width=1400,height=800',
    );
    syncDisplayWindowState();
  }, [syncDisplayWindowState]);
  const [isDisplayReversed, setIsDisplayReversed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('displayReversed') === 'true';
  });
  const [channelReady, setChannelReady] = useState(false);
  const [showSceneModal, setShowSceneModal] = useState(false);
  const [sceneForm, setSceneForm] = useState({ name: '' });
  const [showDeleteSceneModal, setShowDeleteSceneModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    id: null,
    name: '',
    group: '',
    grayscale: false,
    visible: true,
    goodPreview: null,
    evilPreview: null,
    neutralPreview: null,
    goodFile: null,
    evilFile: null,
    neutralFile: null,
    goodImageId: null,
    evilImageId: null,
    neutralImageId: null,
    disposition: 'good',
    showInitial: true,
    size: 1,
    conditions: [],
    dhHpMax: 0,
    dhHpUsed: 0,
    dhStressMax: 0,
    dhStressUsed: 0,
    dhExtra1Label: '',
    dhExtra1Max: 0,
    dhExtra1Used: 0,
    dhExtra2Label: '',
    dhExtra2Max: 0,
    dhExtra2Used: 0,
    dhThresholdMajor: 0,
    dhThresholdSevere: 0,
  });
  const [isEditingToken, setIsEditingToken] = useState(false);
  const [showLayerModal, setShowLayerModal] = useState(false);
  const [layerForm, setLayerForm] = useState({
    id: null,
    name: '',
    group: '',
    type: 'image',
    z: 1,
    bgColor: '',
    preview: null,
    file: null,
    imageId: null,
    centerOnShow: false,
    // text layer fields
    text: '',
    font: 'sans-serif',
    fontSize: 36,
    color: '#ffffff',
    // draw layer fields
    drawColor: '#e94f37',
    strokeWidth: 4,
    paths: [],
  });
  const [isEditingLayer, setIsEditingLayer] = useState(false);
  const [drawingLayerId, setDrawingLayerId] = useState(null);
  const [showEffectModal, setShowEffectModal] = useState(false);
  const [showBattleEffectModal, setShowBattleEffectModal] = useState(false);
  const [battleEffectForm, setBattleEffectForm] = useState(() => buildBattleEffectDefaults());
  const [isEditingBattleEffect, setIsEditingBattleEffect] = useState(false);
  const [showDiceHistoryModal, setShowDiceHistoryModal] = useState(false);
  const [showGmDiceModal, setShowGmDiceModal] = useState(false);
  const [showSystemSelectModal, setShowSystemSelectModal] = useState(false);
  const [showSystemDmScreenModal, setShowSystemDmScreenModal] = useState(false);
  const [showLoadTokensModal, setShowLoadTokensModal] = useState(false);
  const [showEditScenesModal, setShowEditScenesModal] = useState(false);
  const [showWorldMapModal, setShowWorldMapModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [worldMap, setWorldMap] = useState(null);
  const [library, setLibrary] = useState(null);
  const [worldMapShared, setWorldMapShared] = useState(false);
  const [libraryShared, setLibraryShared] = useState(false);
  const [mapOverlayActive, setMapOverlayActive] = useState(false);
  const mapOverlayViewRef = useRef({ panX: 0, panY: 0, zoom: 1 });
  const [rangeSourceId, setRangeSourceId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [allowPlayerControls, setAllowPlayerControls] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('orchestratorBreak') === 'true';
  });
  const [isPreparingSession, setIsPreparingSession] = useState(false);
  const [sessionRolls, setSessionRolls] = useState([]);
  const [paintingBattleEffectId, setPaintingBattleEffectId] = useState(null);
  const [pings, setPings] = useState([]);
  const [showNamesMode, setShowNamesMode] = useState(false);
  const [measuringMode, setMeasuringMode] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const displayZoom = gmViewState.zoom;
  const [autoCenterOnSelect, setAutoCenterOnSelect] = useState(false);

  // Grid configuration
  const defaultGridConfig = {
    cols: DEFAULT_GRID_COLS,
    rows: DEFAULT_GRID_ROWS,
    type: DEFAULT_GRID_TYPE,
  };
  const [gridConfig, setGridConfig] = useState(defaultGridConfig);
  const [showGridConfigModal, setShowGridConfigModal] = useState(false);

  // Tracker state (Daggerheart only – persists in localStorage, not per-scene)
  const [trackers, setTrackers] = useState(() => getInitialTrackers(null));
  const trackersRef = useRef(trackers);
  const [showTrackerModal, setShowTrackerModal] = useState(false);
  const [trackerForm, setTrackerForm] = useState({ id: null, name: '', size: 12, color: '#e94f37', group: '' });
  const [isEditingTracker, setIsEditingTracker] = useState(false);

  // Gameplay sync state
  const [isGameplaySyncEnabled, setIsGameplaySyncEnabled] = useState(() =>
    getGameplaySyncEnabled()
  );
  const syncCheckDoneRef = useRef(false);
  // Ref mirrors so callbacks with empty deps can read current sync state
  const isGameplaySyncEnabledRef = useRef(isGameplaySyncEnabled);
  const syncSceneToCloudRef = useRef(null);

  // Cloud feature state
  const [userTier, setUserTier] = useState(null);
  const [isTierLoading, setIsTierLoading] = useState(true);
  const [showAssetManagerModal, setShowAssetManagerModal] = useState(false);
  const [showCloudAccessModal, setShowCloudAccessModal] = useState(false);
  const [showGameplaySyncErrorModal, setShowGameplaySyncErrorModal] = useState(false);
  const [isSyncingAssets, setIsSyncingAssets] = useState(false);

  const managerRef = useRef(null);
  const managerBoardRef = useRef(null);
  const [managerViewportSize, setManagerViewportSize] = useState({ width: 0, height: 0 });
  const boardAspectRatio = gridConfig.type === 'hex'
    ? hexGridAspectRatio(gridConfig.cols, gridConfig.rows)
    : gridConfig.cols / gridConfig.rows;
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [layerFlipState, setLayerFlipState] = useState(null);
  const [dhTooltipTokenId, setDhTooltipTokenId] = useState(null);
  const channelRef = useRef(null);
  const channelClosedRef = useRef(false);
  const safePostMessage = useCallback((payload) => {
    const channel = channelRef.current;
    if (!channel || channelClosedRef.current) return;
    try {
      channel.postMessage(payload);
    } catch (error) {
      console.warn('BroadcastChannel send failed', error);
    }
  }, []);
  const emitRangeSource = useCallback((tokenId) => {
    safePostMessage({ type: 'rangeSource', tokenId });
  }, [safePostMessage]);
  const migratedOnceRef = useRef(false);
  const layerVisibilityRef = useRef(new Map());
  const pendingCenterRef = useRef(false);

  const sessionInitRef = useRef(false);
  const processedCommandsRef = useRef(new Set());
  const processingRef = useRef(false);
  const unsubscribeCommandsRef = useRef(null);
  const unsubscribeRollsRef = useRef(null);
  // Keyed by rollId; holds info needed to complete Firebase write + toast after
  // the Display signals rollComplete (i.e. after the 3D animation finishes).
  const pendingRollsRef = useRef(new Map());
  // Mirror of sessionToken state kept in a ref so async/stale-closure callbacks
  // (e.g. the BroadcastChannel onmessage handler) always read the current value.
  const sessionTokenRef = useRef(null);
  // Keep sessionTokenRef in sync with the sessionToken state.
  useEffect(() => {
    sessionTokenRef.current = sessionToken;
  }, [sessionToken]);

  const emitToastToDisplay = useCallback((message, options, variant = 'default') => {
    safePostMessage({
      type: 'toast',
      message,
      options,
      variant,
    });
  }, [safePostMessage]);

  const handleGmRoll = useCallback(async (command) => {
    if (!sessionToken) return;
    const trimmed = String(command ?? '').trim();
    if (!trimmed) return;

    const gmDisplayName = localStorage.getItem('displayName') || '';
    const gmLabel = gmDisplayName ? `GM (${gmDisplayName})` : 'Game Master';

    const parsed = parseDiceCommand(trimmed);
    const resolved = resolveRoll(parsed);

    if (!resolved.ok) {
      const message = `${gmLabel}: ${resolved.error}`;
      const options = {
        style: {
          background: GM_THEME.backgroundSoft,
          color: '#fff',
          border: `2px solid ${GM_THEME.accent}`,
        },
      };
      toast.error(message, options);
      emitToastToDisplay(message, options, 'error');
      return;
    }

    const baseRecord = {
      username: gmLabel,
      command: trimmed,
      kind: resolved.kind,
      theme: {
        accent: GM_THEME.accent,
        background: GM_THEME.background,
      },
    };

    const rollId = `${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;

    // Send roll event to Display with animation + parsed params so the Display
    // can compute the correct displayText from actual dice values via onRollComplete.
    safePostMessage({
      type: 'rollEvent',
      rollId,
      roll: { ...baseRecord, animation: resolved.animation, parsed },
    });

    // Defer Firebase write and GM toast until the Display signals rollComplete.
    pendingRollsRef.current.set(rollId, { baseRecord, theme: GM_THEME });
  }, [sessionToken, emitToastToDisplay, safePostMessage]);

  const darkModeSuffix = mode === 'dark' ? '_dark' : '';

    const scenesMeta = useMemo(
    () =>
      (Array.isArray(scenes) ? scenes : [])
        .map((scene) => ({ id: scene?.id, name: scene?.name ?? '' }))
        .filter((scene) => !!scene.id),
    [scenes]
  );

  const currentScene = useMemo(
    () => scenes.find((scene) => scene.id === currentSceneId) || null,
    [scenes, currentSceneId]
  );

  const gmViewSceneRef = useRef(currentSceneId);

  useEffect(() => {
    const nextView = loadGridViewState(currentSceneId, 'gm');
    gmViewSceneRef.current = currentSceneId;
    gmViewStateRef.current = nextView;
    setGmViewSceneId(currentSceneId);
    setGmViewState(nextView);
    setIsZoomed(nextView.zoom > 1);
    if (channelReady && currentSceneId) {
      safePostMessage({ type: 'gridViewState', sceneId: currentSceneId, viewState: nextView });
    }
  }, [currentSceneId, channelReady, safePostMessage]);

  useEffect(() => {
    if (!currentSceneId || gmViewSceneId !== currentSceneId) return undefined;
    const timeoutId = window.setTimeout(() => {
      saveGridViewState(currentSceneId, gmViewState, 'gm');
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [currentSceneId, gmViewSceneId, gmViewState]);

  const updateGridViewState = useCallback((nextView) => {
    if (!currentSceneId || gmViewSceneId !== currentSceneId) return;
    const displayViewport = isDisplayOpen ? displayViewportRef.current : null;
    const normalized = normalizeGridViewState(nextView, {
      width: displayViewport?.width || managerViewportSize.width,
      height: displayViewport?.height || managerViewportSize.height,
      aspectRatio: boardAspectRatio,
      fitMode: isDisplayOpen ? 'cover' : 'contain',
    });
    gmViewStateRef.current = normalized;
    setGmViewState(normalized);
    safePostMessage({ type: 'gridViewState', sceneId: currentSceneId, viewState: normalized });
  }, [boardAspectRatio, currentSceneId, gmViewSceneId, isDisplayOpen, managerViewportSize, safePostMessage]);

  useEffect(() => {
    if (isDisplayOpen) return;
    const boardRect = managerBoardRef.current?.getBoundingClientRect();
    const viewport = getGridViewportMetrics({
      width: boardRect?.width || managerViewportSize.width,
      height: boardRect?.height || managerViewportSize.height,
      aspectRatio: boardAspectRatio,
      zoom: gmViewState.zoom,
      centerX: gmViewState.centerX,
      centerY: gmViewState.centerY,
      fitMode: 'contain',
    });
    displayViewportRef.current = viewport;
    displayViewportStore.set(viewport);
  }, [
    boardAspectRatio,
    displayViewportStore,
    gmViewState.centerX,
    gmViewState.centerY,
    gmViewState.zoom,
    isDisplayOpen,
    managerViewportSize.height,
    managerViewportSize.width,
  ]);

  const updateDisplayZoom = useCallback((nextZoom) => {
    updateGridViewState({
      ...gmViewStateRef.current,
      zoom: clampGridZoom(nextZoom),
    });
  }, [updateGridViewState]);

  const stepDisplayZoom = useCallback((direction) => {
    updateDisplayZoom(gmViewStateRef.current.zoom + direction * GRID_ZOOM_STEP);
  }, [updateDisplayZoom]);

  const selectDisplayZoomPreset = useCallback((direction) => {
    updateDisplayZoom(getAdjacentGridZoomPreset(gmViewStateRef.current.zoom, direction));
  }, [updateDisplayZoom]);

  useEffect(() => {
    setGridConfig(currentScene?.gridConfig || defaultGridConfig);
  }, [currentScene]);

  const handleToggleZoom = useCallback(() => {
    const nextIsZoomed = !isZoomed;
    setIsZoomed(nextIsZoomed);
    updateGridViewState({
      ...gmViewStateRef.current,
      zoom: 1,
      centerX: 0.5,
      centerY: 0.5,
    });
  }, [gmViewState, isZoomed, updateGridViewState]);

  const moveDisplayViewport = useCallback((scrollLeft, scrollTop) => {
    const viewport = displayViewportRef.current;
    if (!viewport) return;
    const contentWidth = Number(viewport.contentWidth) || 0;
    const contentHeight = Number(viewport.contentHeight) || 0;
    const viewportWidth = Number(viewport.width) || 0;
    const viewportHeight = Number(viewport.height) || 0;
    const currentView = gmViewStateRef.current;
    updateGridViewState({
      ...currentView,
      centerX: contentWidth
        ? ((Number(scrollLeft) || 0) + viewportWidth / 2) / contentWidth
        : currentView.centerX,
      centerY: contentHeight
        ? ((Number(scrollTop) || 0) + viewportHeight / 2) / contentHeight
        : currentView.centerY,
    });
  }, [updateGridViewState]);

  const resetDisplayZoom = useCallback(() => {
    setIsZoomed(false);
    updateGridViewState(DEFAULT_GRID_VIEW_STATE);
  }, [updateGridViewState]);

  const persistScene = useCallback((scene) => {
    setScenes((prev) => {
      const updated = prev.map((item) => (item.id === scene.id ? scene : item));
      saveScenes(updated);
      safePostMessage({
        type: 'sceneUpdate',
        sceneId: scene.id,
        scene,
      });
      return updated;
    });
    // Background sync – only runs when sync is enabled
    if (isGameplaySyncEnabledRef.current && syncSceneToCloudRef.current) {
      syncSceneToCloudRef.current(scene);
    }
  }, []);

  // ── Gameplay sync helpers ──────────────────────────────────────────────

  /**
   * Save the scene gameplay payload to Firestore when sync is enabled.
   */
  const syncSceneToCloud = useCallback(
    async (scene) => {
      const username = localStorage.getItem('username');
      if (!username) return;

      // Upload any images that don't yet have a remote URL
      for (const layer of scene.layers || []) {
        if (layer.imageId && !getAssetUrl(layer.imageId)) {
          try {
            const dataUrl = await getImage(layer.imageId);
            if (dataUrl) {
              const remoteUrl = await uploadToStorage(dataUrl);
              setAssetUrl(layer.imageId, remoteUrl);
            }
          } catch (err) {
            console.warn('[GameplaySync] Asset upload failed for', layer.imageId, err);
          }
        }
      }
      for (const token of scene.tokens || []) {
        for (const id of [
          token.goodImageId || token.imageId,
          token.evilImageId || token.altImageId,
          token.neutralImageId,
        ]) {
          if (id && !getAssetUrl(id)) {
            try {
              const dataUrl = await getImage(id);
              if (dataUrl) {
                const remoteUrl = await uploadToStorage(dataUrl);
                setAssetUrl(id, remoteUrl);
              }
            } catch (err) {
              console.warn('[GameplaySync] Asset upload failed for', id, err);
            }
          }
        }
      }

      try {
        await saveSceneGameplay(db, username, scene);
      } catch (err) {
        console.warn('[GameplaySync] Failed saving scene to Firestore', err);
      }
    },
    []
  );

  /**
   * Load all synced scenes from Firestore, process their image URLs, and
   * replace the local scene list.  Shows the PreparingSession overlay.
   */
  const loadAllScenesFromCloud = useCallback(async () => {
    const username = localStorage.getItem('username');
    if (!username) return;

    setIsPreparingSession(true);
    try {
      const payloads = await loadScenesGameplay(db, username);
      if (!payloads.length) {
        return;
      }

      const processed = await Promise.all(
        payloads.map((payload) =>
          processScenePayload(payload, payload.id || `scene_${Date.now()}`)
        )
      );

      setScenes(processed);
      saveScenes(processed);
      if (processed.length > 0) {
        const firstId = processed[0].id;
        setCurrentSceneId(firstId);
        setStoredSceneId(firstId);
      }
    } catch (err) {
      console.error('[GameplaySync] Failed loading scenes from cloud', err);
      toast.error('Failed to load scenes from cloud. Please try again.');
    } finally {
      setIsPreparingSession(false);
    }
  }, []);

  /**
   * Enable gameplay sync: upload all current local scenes to Firestore,
   * persist the enabled flag, and set state.
   */
  const handleEnableGameplaySync = useCallback(async () => {
    const username = localStorage.getItem('username');
    if (!username) return;

    setIsSyncingAssets(true);
    try {
      const currentScenes = loadScenes();
      await saveAllScenesGameplay(db, username, currentScenes);
      setGameplaySyncEnabledLocal(true);
      setIsGameplaySyncEnabled(true);
      isGameplaySyncEnabledRef.current = true;
      toast.success('Gameplay sync enabled!', { toastId: 'gameplay-sync-enabled' });
    } catch (err) {
      console.error('[GameplaySync] Failed enabling sync', err);
      toast.error('Failed to enable gameplay sync. Please try again.');
    } finally {
      setIsSyncingAssets(false);
    }
  }, []);

  /**
   * Disable gameplay sync: clear Firestore scenes payload and update local state.
   */
  const handleDisableGameplaySync = useCallback(async () => {
    const username = localStorage.getItem('username');
    if (!username) return;

    setGameplaySyncEnabledLocal(false);
    setIsGameplaySyncEnabled(false);
    isGameplaySyncEnabledRef.current = false;
    toast.info('Gameplay sync disabled.', { toastId: 'gameplay-sync-disabled' });
  }, []);

  /**
   * Conflict resolution – store local scenes to cloud (overwrite cloud).
   */
  const handleStoreLocalToCloud = useCallback(async () => {
    const username = localStorage.getItem('username');
    if (!username) return;

    setIsPreparingSession(true);
    try {
      const currentScenes = loadScenes();
      await saveAllScenesGameplay(db, username, currentScenes);
      setGameplaySyncEnabledLocal(true);
      setIsGameplaySyncEnabled(true);
      isGameplaySyncEnabledRef.current = true;
      setShowGameplaySyncErrorModal(false);
      toast.success('Local scenes uploaded. Gameplay sync re-enabled.', { toastId: 'sync-store-ok' });
    } catch (err) {
      console.error('[GameplaySync] Failed storing local scenes', err);
      toast.error('Failed to store scenes to cloud.');
    } finally {
      setIsPreparingSession(false);
    }
  }, []);

  /**
   * Conflict resolution – load scenes from cloud (overwrite local).
   */
  const handleLoadFromCloudAndResolve = useCallback(async () => {
    await loadAllScenesFromCloud();
    setGameplaySyncEnabledLocal(true);
    setIsGameplaySyncEnabled(true);
    isGameplaySyncEnabledRef.current = true;
    setShowGameplaySyncErrorModal(false);
    toast.success('Cloud scenes loaded. Gameplay sync re-enabled.', { toastId: 'sync-load-ok' });
  }, [loadAllScenesFromCloud]);

  /**
   * Conflict resolution – disable sync without changing local data.
   */
  const handleDisableSyncFromConflict = useCallback(() => {
    setGameplaySyncEnabledLocal(false);
    setIsGameplaySyncEnabled(false);
    isGameplaySyncEnabledRef.current = false;
    setShowGameplaySyncErrorModal(false);
    toast.info('Gameplay sync disabled.', { toastId: 'sync-conflict-disabled' });
  }, []);

  /**
   * Handle Asset Management button click – check user tier first.
   */
  const handleAssetManagement = useCallback(() => {
    if (isTierLoading) {
      toast.info('Verifying cloud access, please wait…', { toastId: 'tier-loading' });
      return;
    }
    if (userTier === '1') {
      setShowAssetManagerModal(true);
    } else {
      setShowCloudAccessModal(true);
    }
  }, [isTierLoading, userTier]);

  // ── Load from cloud on login (with conflict detection) ─────────────────
  useEffect(() => {
    if (syncCheckDoneRef.current) return;
    syncCheckDoneRef.current = true;

    const username = localStorage.getItem('username');
    if (!username) return;

    const localScenes = loadScenes();

    if (!getGameplaySyncEnabled()) {
      // Sync not enabled — nothing to do
      return;
    }

    (async () => {
      try {
        const payloads = await loadScenesGameplay(db, username);

        if (!payloads.length) {
          // Cloud is empty — upload local scenes if any
          if (localScenes.length > 0) {
            await saveAllScenesGameplay(db, username, localScenes);
          }
          return;
        }

        if (localScenes.length === 0) {
          // Local is empty — download from cloud
          await loadAllScenesFromCloud();
          return;
        }

        // Both local and cloud have data — check for conflict
        const cloudIds = new Set(payloads.map((p) => p.id).filter(Boolean));
        const localIds = new Set(localScenes.map((s) => s.id).filter(Boolean));
        const hasConflict =
          localIds.size !== cloudIds.size ||
          [...localIds].some((id) => !cloudIds.has(id));

        if (hasConflict) {
          // Stop sync and show conflict modal
          setGameplaySyncEnabledLocal(false);
          setIsGameplaySyncEnabled(false);
          isGameplaySyncEnabledRef.current = false;
          setShowGameplaySyncErrorModal(true);
          toast.warn('Gameplay sync paused — conflict detected. Please resolve it.', {
            toastId: 'sync-conflict-detected',
            autoClose: 8000,
          });
        }
        // If no conflict, scenes are already in sync — nothing extra to do
      } catch (err) {
        console.error('[GameplaySync] Conflict check failed', err);
      }
    })();
  }, [loadAllScenesFromCloud]);

  // ── World map helpers ──────────────────────────────────────────────────────

  useEffect(() => {
    const username = localStorage.getItem('username');
    if (!username) return;
    loadWorldMap(db, username)
      .then((wm) => { if (wm) setWorldMap(wm); })
      .catch((err) => console.warn('[WorldMap] Load failed', err));
  }, []);

  const handleSaveWorldMap = useCallback(async (updatedWorldMap) => {
    setWorldMap(updatedWorldMap);
    const username = localStorage.getItem('username');
    if (!username) return;
    try {
      await saveWorldMap(db, username, updatedWorldMap);
      const playerWm = buildPlayerWorldMap(updatedWorldMap);
      if (sessionToken) {
        await updateSessionDoc(db, sessionToken, {
          worldMap: worldMapShared ? playerWm : null,
          worldMapShared,
        });
      }
      // If overlay is active, push updated player-visible map to the display
      setMapOverlayActive((active) => {
        if (active) {
          safePostMessage({
            type: 'worldMapOverlay',
            active: true,
            worldMap: playerWm,
            view: mapOverlayViewRef.current,
          });
        }
        return active;
      });
    } catch (err) {
      console.warn('[WorldMap] Save failed', err);
      toast.error('Failed to save world map.');
    }
  }, [safePostMessage, sessionToken, worldMapShared]);

  const handleToggleWorldMapShare = useCallback(async () => {
    const next = !worldMapShared;
    setWorldMapShared(next);
    if (!sessionToken) return;
    try {
      await updateSessionDoc(db, sessionToken, {
        worldMap: next ? buildPlayerWorldMap(worldMap) : null,
        worldMapShared: next,
      });
    } catch (err) {
      setWorldMapShared(!next);
      toast.error('Failed to update map sharing.');
    }
  }, [sessionToken, worldMap, worldMapShared]);

  const handleMapViewChange = useCallback((view) => {
    mapOverlayViewRef.current = view;
    setMapOverlayActive((active) => {
      if (active) {
        safePostMessage({ type: 'worldMapOverlayView', view });
      }
      return active;
    });
  }, [safePostMessage]);

  const handleToggleDisplayOverlay = useCallback(() => {
    setMapOverlayActive((prev) => {
      const next = !prev;
      if (next) {
        setWorldMap((wm) => {
          const playerWm = buildPlayerWorldMap(wm);
          safePostMessage({
            type: 'worldMapOverlay',
            active: true,
            worldMap: playerWm,
            view: mapOverlayViewRef.current,
          });
          return wm;
        });
      } else {
        safePostMessage({ type: 'worldMapOverlay', active: false });
      }
      return next;
    });
  }, [safePostMessage]);

  // ── Library helpers ────────────────────────────────────────────────────────

  useEffect(() => {
    const username = localStorage.getItem('username');
    if (!username) return;
    loadLibrary(db, username)
      .then((lib) => { if (lib) setLibrary(lib); })
      .catch((err) => console.warn('[Library] Load failed', err));
  }, []);

  const handleSaveLibrary = useCallback(async (updatedLibrary) => {
    setLibrary(updatedLibrary);
    const username = localStorage.getItem('username');
    if (!username) return;
    try {
      await saveLibrary(db, username, updatedLibrary);
      if (sessionToken) {
        const playerLib = buildPlayerLibrary(updatedLibrary);
        await updateSessionDoc(db, sessionToken, {
          library: libraryShared ? playerLib : null,
          libraryShared,
        });
      }
    } catch (err) {
      console.warn('[Library] Save failed', err);
      toast.error('Failed to save library.');
    }
  }, [libraryShared, sessionToken]);

  const handleToggleLibraryShare = useCallback(async () => {
    const next = !libraryShared;
    setLibraryShared(next);
    if (!sessionToken) return;
    try {
      await updateSessionDoc(db, sessionToken, {
        library: next ? buildPlayerLibrary(library) : null,
        libraryShared: next,
      });
    } catch (err) {
      setLibraryShared(!next);
      toast.error('Failed to update resource sharing.');
    }
  }, [library, libraryShared, sessionToken]);

  // ── Load user tier on mount ────────────────────────────────────────────
  useEffect(() => {
    const username = localStorage.getItem('username');
    if (!username) {
      setIsTierLoading(false);
      return;
    }

    fetchUserTier(db, username)
      .then((tier) => setUserTier(tier))
      .catch((err) => {
        console.warn('[Provisioning] Failed fetching user tier', err);
        toast.warn('Could not verify cloud access. Please refresh to retry.', {
          toastId: 'tier-fetch-failed',
        });
      })
      .finally(() => setIsTierLoading(false));
  }, []);
  // Keep refs in sync with latest state/callbacks so persistScene (empty deps) can use them
  useEffect(() => {
    isGameplaySyncEnabledRef.current = isGameplaySyncEnabled;
  }, [isGameplaySyncEnabled]);

  useEffect(() => {
    syncSceneToCloudRef.current = syncSceneToCloud;
  }, [syncSceneToCloud]);

  useEffect(() => {
    const username = localStorage.getItem('username');
    const system = localStorage.getItem('system');
    if (!username || system !== 'orchestrator') {
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('appearance', JSON.stringify({ mode }));
    const tag = document.querySelector('meta[name="theme-color"]');
    if (tag) tag.setAttribute('content', mode === 'dark' ? '#393e41' : '#e94f37');
  }, [mode]);

  // Create a fresh player session token on orchestrator entry (guarded for React 18 StrictMode).
  useEffect(() => {
    if (!channelReady) return;
    if (sessionInitRef.current) return;
    sessionInitRef.current = true;

    const createdBy = localStorage.getItem('username') || null;

    (async () => {
      try {
        const token = await createSession(db, {
          createdBy,
          scenesMeta,
          currentSceneId: currentSceneId ?? null,
        });
        setSessionToken(token);
        localStorage.setItem('orchestratorSessionToken', token);
        safePostMessage({ type: 'sessionUpdate', token });
        try {
          await updateSessionDoc(db, token, {
            allowPlayerControls: false,
            scene: null,
            worldMapShared: false,
            libraryShared: false,
          });
        } catch (error) {
          console.warn('Failed initializing player controls state', error);
        }
      } catch (error) {
        console.error('Failed creating session', error);
        toast.error('Failed creating a player session.');
        emitToastToDisplay('Failed creating a player session.', undefined, 'error');
      }
    })();
  }, [channelReady, currentSceneId, scenesMeta]);

  // Keep the session snapshot updated when scenes or currentSceneId changes.
  useEffect(() => {
    if (!sessionToken) return;
    let cancelled = false;
    const id = setTimeout(() => {
      if (cancelled) return;
      updateSessionSnapshot(db, sessionToken, {
        scenesMeta,
        currentSceneId: currentSceneId ?? null,
      }).catch((error) => {
        // Non-fatal; session can still be used for dice.
        console.warn('Failed updating session snapshot', error);
      });
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [currentSceneId, scenesMeta, sessionToken]);

  useEffect(() => {
    if (!sessionToken || !currentScene || (!allowPlayerControls && !isGameplaySyncEnabled)) return;
    const syncEnabled = isGameplaySyncEnabled;
    const payload = syncEnabled
      ? buildFullPlayerScenePayload(currentScene)
      : buildPlayerScenePayload(currentScene);
    updateSessionDoc(db, sessionToken, {
      scene: payload,
      gameplaySyncEnabled: syncEnabled,
    }).catch((error) => {
      console.warn('Failed syncing scene for player controls', error);
    });
  }, [allowPlayerControls, currentScene, isGameplaySyncEnabled, sessionToken]);

  useEffect(() => {
    if (!sessionToken) return;
    updateSessionDoc(db, sessionToken, {
      gameplaySyncEnabled: isGameplaySyncEnabled,
    }).catch((error) => {
      console.warn('Failed syncing gameplay sync state for players', error);
    });
  }, [isGameplaySyncEnabled, sessionToken]);

  // Keep dice history in sync for the session.
  useEffect(() => {
    if (unsubscribeRollsRef.current) {
      unsubscribeRollsRef.current();
      unsubscribeRollsRef.current = null;
    }

    if (!sessionToken) {
      setSessionRolls([]);
      return undefined;
    }

    const unsub = listenSessionRolls(db, sessionToken, (docs) => {
      if (!Array.isArray(docs)) return;
      setSessionRolls(docs);
    });

    unsubscribeRollsRef.current = unsub;
    return () => {
      if (unsubscribeRollsRef.current) {
        unsubscribeRollsRef.current();
        unsubscribeRollsRef.current = null;
      }
    };
  }, [sessionToken]);

  // Handle grid ping
  const handlePingGrid = useCallback((x, y, source = 'gm') => {
    const pingId = `ping_${Date.now()}_${Math.random()}`;
    const newPing = { id: pingId, x, y, source, createdAt: Date.now() };

    setPings((prevPings) => [...prevPings, newPing]);

    // Auto-remove ping after 3 seconds
    setTimeout(() => {
      setPings((prevPings) => prevPings.filter((p) => p.id !== pingId));
    }, 3000);

    // Broadcast ping to display
    safePostMessage({
      type: 'ping',
      ping: newPing,
    });
  }, [safePostMessage]);

  useEffect(() => {
    if (sessionToken) {
      updateSessionDoc(db, sessionToken, { pings }).catch(() => {});
    }
  }, [db, pings, sessionToken]);

  // Listen for player commands and process them exactly once.
  useEffect(() => {
    if (!sessionToken) return;

    if (unsubscribeCommandsRef.current) {
      unsubscribeCommandsRef.current();
      unsubscribeCommandsRef.current = null;
    }

    processedCommandsRef.current = new Set();

    const processDocs = async (docs) => {
      const pending = docs.filter((d) => d && d.processed !== true);
      const fresh = pending.filter((d) => !processedCommandsRef.current.has(d.id));
      if (!fresh.length) return;
      if (processingRef.current) return;

      processingRef.current = true;
      try {
        for (const cmd of fresh) {
          processedCommandsRef.current.add(cmd.id);

          const rawCommand = String(cmd.command ?? '').trim();
          const parts = rawCommand.split(/\s+/);
          const isMoveCommand = parts.length >= 4 && parts[0].toLowerCase() === 'move';
          const isSetPosCommand = parts.length >= 4 && parts[0].toLowerCase() === 'setpos';
          const isPingCommand = parts.length >= 3 && parts[0].toLowerCase() === 'ping';
          const isMeasureCommand = parts.length >= 2 && parts[0].toLowerCase() === 'measure';
          const isBattleEffectCommand = parts.length >= 5 && parts[0].toLowerCase() === 'battle_effect';

          if (isPingCommand) {
            const x = Number(parts[1]);
            const y = Number(parts[2]);
            const pingMaxX = gridConfig.cols;
            const pingMaxY = gridConfig.rows;

            if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || x >= pingMaxX || y < 0 || y >= pingMaxY) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: `Invalid ping coordinates. Use: PING x y (0-${pingMaxX - 1}, 0-${pingMaxY - 1})`,
                processedKind: 'ping',
              });
              continue;
            }

            if (!allowPlayerControls) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Player controls are disabled.',
                processedKind: 'ping',
              });
              continue;
            }

            // Create and broadcast ping
            handlePingGrid(x, y, 'player');

            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: 'ping',
            });
            continue;
          }

          if (isMoveCommand) {
            const tokenId = parts[1];
            const dx = Number(parts[2]);
            const dy = Number(parts[3]);

            if (!allowPlayerControls) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Player controls are disabled.',
                processedKind: 'move',
              });
              continue;
            }

            if (!currentScene) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No scene loaded.',
                processedKind: 'move',
              });
              continue;
            }

            const targetToken = (currentScene.tokens || []).find(
              (token) => token.id === tokenId && token.visible !== false
            );
            if (!targetToken) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Token not available for players.',
                processedKind: 'move',
              });
              continue;
            }

            const updatedScene = nudgeTokenOnScene(
              currentScene,
              tokenId,
              dx,
              dy,
              gridConfig.cols,
              gridConfig.rows,
              gridConfig.type
            );
            if (!updatedScene) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No movement applied.',
                processedKind: 'move',
              });
              continue;
            }

            persistScene(updatedScene);
            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: 'move',
            });
            continue;
          }

          if (isSetPosCommand) {
            const tokenId = parts[1];
            const newX = Number(parts[2]);
            const newY = Number(parts[3]);

            if (!allowPlayerControls) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Player controls are disabled.',
                processedKind: 'setpos',
              });
              continue;
            }

            if (!currentScene) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No scene loaded.',
                processedKind: 'setpos',
              });
              continue;
            }

            const setposToken = (currentScene.tokens || []).find(
              (token) => token.id === tokenId && token.visible !== false
            );
            if (!setposToken) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Token not available for players.',
                processedKind: 'setpos',
              });
              continue;
            }

            const tokenSize = getGridUnitsForSize(setposToken.size);
            const positionLimit = gridConfig.type === 'hex' ? 1 : tokenSize;
            const clampedX = Math.min(gridConfig.cols - positionLimit, Math.max(0, Math.round(newX)));
            const clampedY = Math.min(gridConfig.rows - positionLimit, Math.max(0, Math.round(newY)));

            if (setposToken.x === clampedX && setposToken.y === clampedY) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No movement applied.',
                processedKind: 'setpos',
              });
              continue;
            }

            const setposUpdatedScene = {
              ...currentScene,
              tokens: currentScene.tokens.map((t) =>
                t.id === tokenId ? { ...t, x: clampedX, y: clampedY } : t
              ),
            };

            persistScene(setposUpdatedScene);
            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: 'setpos',
            });
            continue;
          }

          if (isMeasureCommand) {
            const arg1 = String(parts[1] ?? '').trim();
            const modeArg = arg1.toLowerCase();
            const tokenArg = modeArg === 'on' ? String(parts[2] ?? '').trim() : arg1;

            if (!allowPlayerControls) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Player controls are disabled.',
                processedKind: 'measure',
              });
              continue;
            }

            if (!currentScene) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No scene loaded.',
                processedKind: 'measure',
              });
              continue;
            }

            if (gridConfig.type === 'hex' && modeArg !== 'off') {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Measurements are disabled on hexagonal grids.',
                processedKind: 'measure',
              });
              continue;
            }

            if (!tokenArg) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Missing token for measure command.',
                processedKind: 'measure',
              });
              continue;
            }

            if (modeArg === 'off') {
              setMeasuringMode(false);
              setRangeSourceId(null);
              emitRangeSource(null);
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: null,
                processedKind: 'measure',
              });
              continue;
            }

            const targetToken = (currentScene.tokens || []).find(
              (token) => token.id === tokenArg && token.visible !== false
            );

            if (!targetToken) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Token not available for players.',
                processedKind: 'measure',
              });
              continue;
            }

            // Keep selected token aligned so manager-side sync effects do not immediately clear player-measuring.
            setSelectedTokenId(tokenArg);
            setMeasuringMode(true);
            setShowNamesMode(false);
            setRangeSourceId(tokenArg);
            emitRangeSource(tokenArg);

            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: 'measure',
            });
            continue;
          }

          if (isBattleEffectCommand) {
            // Command: BATTLE_EFFECT <tokenId> <shape> <size> <color> <opacity>
            const tokenId = String(parts[1] ?? '').trim();
            const shape = String(parts[2] ?? '').trim().toLowerCase();
            const size = clampEffectSize(parts[3]);
            const color = String(parts[4] ?? '#9fb3c8').trim();
            const opacity = clampEffectOpacity(parts[5]);

            if (!allowPlayerControls) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Player controls are disabled.',
                processedKind: 'battle_effect',
              });
              continue;
            }

            if (!currentScene) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'No scene loaded.',
                processedKind: 'battle_effect',
              });
              continue;
            }

            if (!VALID_EFFECT_SHAPES.includes(shape)) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: `Invalid shape. Use one of: ${VALID_EFFECT_SHAPES.join(', ')}.`,
                processedKind: 'battle_effect',
              });
              continue;
            }

            const targetToken = (currentScene.tokens || []).find(
              (token) => token.id === tokenId && token.visible !== false
            );

            if (!targetToken) {
              await markCommandProcessed(db, sessionToken, cmd.id, {
                processedError: 'Token not available for players.',
                processedKind: 'battle_effect',
              });
              continue;
            }

            const cx = Math.round(Number(targetToken.x) || 0);
            const cy = Math.round(Number(targetToken.y) || 0);
            const cells = generateShapeCells(shape, size, cx, cy);

            const bfxPlayer = String(cmd.displayName || cmd.username || 'anonymous');
            const effectPayload = {
              id: `bfx_player_${Date.now()}`,
              name: `${bfxPlayer} – ${shape}`,
              kind: 'custom',
              color,
              opacity,
              visible: true,
              group: '',
              shape,
              size,
              cells,
            };

            const updatedScene = {
              ...currentScene,
              battleEffects: [...(currentScene.battleEffects || []), effectPayload],
            };
            persistScene(updatedScene);

            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: 'battle_effect',
            });
            continue;
          }

          const player = String(cmd.displayName || cmd.username || 'anonymous');
          const theme = getUserTheme(String(cmd.username ?? 'anonymous'));

          const parsed = parseDiceCommand(cmd.command);
          const resolved = resolveRoll(parsed);

          if (!resolved.ok) {
            const message = `${player}: ${resolved.error}`;
            const options = {
              style: {
                background: theme.backgroundSoft,
                color: '#fff',
                border: `2px solid ${theme.accent}`,
              },
            };
            toast.error(message, options);
            emitToastToDisplay(message, options, 'error');
            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: resolved.error,
            });
            continue;
          }

          const baseRecord = {
            username: player,
            command: String(cmd.command ?? '').trim(),
            kind: resolved.kind,
            theme: {
              accent: theme.accent,
              background: theme.background,
            },
          };

          const rollId = `${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;

          // Send roll event to Display with animation + parsed params so the Display
          // can compute displayText from actual dice values via onRollComplete.
          safePostMessage({
            type: 'rollEvent',
            rollId,
            roll: { ...baseRecord, animation: resolved.animation, parsed },
          });

          // Mark the command as processed immediately so it won't be re-queued.
          // Firebase roll write is deferred until the Display signals rollComplete.
          try {
            await markCommandProcessed(db, sessionToken, cmd.id, {
              processedError: null,
              processedKind: resolved.kind,
            });
          } catch (error) {
            console.warn('Failed marking roll command as processed', error);
          }

          pendingRollsRef.current.set(rollId, { baseRecord, theme });
        }
      } finally {
        processingRef.current = false;
      }
    };

    unsubscribeCommandsRef.current = listenSessionCommands(db, sessionToken, (docs) => {
      processDocs(docs).catch((error) => console.error('Command processing failed', error));
    });

    return () => {
      if (unsubscribeCommandsRef.current) {
        unsubscribeCommandsRef.current();
        unsubscribeCommandsRef.current = null;
      }
    };
  }, [allowPlayerControls, currentScene, emitRangeSource, gridConfig, handlePingGrid, persistScene, sessionToken]);

  useEffect(() => {
    const channel = new BroadcastChannel('orchestrator_channel');
    channelRef.current = channel;
    channelClosedRef.current = false;
    setChannelReady(true);
    return () => {
      setChannelReady(false);
      channelClosedRef.current = true;
      channel.close();
      channelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (migratedOnceRef.current) return;
    migratedOnceRef.current = true;
    (async () => {
      const migrated = await Promise.all(
        scenes.map((scene) => migrateSceneImages(scene))
      );
      setScenes(migrated);
      saveScenes(migrated);
    })();
  }, [currentSceneId]);

  useEffect(() => {
    setStoredSceneId(currentSceneId || null);
    setLayerFlipState(null);
  }, [currentSceneId]);

  useEffect(() => {
    if (selectedTokenId) setLayerFlipState(null);
  }, [selectedTokenId]);

  useEffect(() => {
    if (!currentScene) {
      setPaintingBattleEffectId(null);
      return;
    }
    if (
      paintingBattleEffectId &&
      !(currentScene.battleEffects || []).some(
        (effect) => effect && effect.id === paintingBattleEffectId
      )
    ) {
      setPaintingBattleEffectId(null);
    }
  }, [currentScene, paintingBattleEffectId]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'displayReversed',
        isDisplayReversed ? 'true' : 'false'
      );
    }
    if (channelReady) {
      safePostMessage({
        type: 'displayRotation',
        flipped: isDisplayReversed,
      });
    }
  }, [isDisplayReversed, channelReady, safePostMessage]);
  const toggleDisplayRotation = useCallback(() => {
    setIsDisplayReversed((prev) => !prev);
  }, []);

  const handleToggleNamesMode = useCallback(() => {
    setShowNamesMode((prev) => {
      const next = !prev;
      if (next) {
        // Entering names mode: exit measuring mode and clear range
        setMeasuringMode(false);
        setRangeSourceId(null);
        emitRangeSource(null);
      }
      return next;
    });
  }, [emitRangeSource]);

  useEffect(() => {
    if (sessionToken) {
      updateSessionDoc(db, sessionToken, { showNames: showNamesMode }).catch(() => {});
    }
  }, [db, sessionToken, showNamesMode]);

  const handleToggleMeasuringMode = useCallback(() => {
    if (gridConfig.type === 'hex') return;
    setMeasuringMode((prev) => {
      const next = !prev;
      if (next) {
        // Entering measuring mode: exit names mode
        setShowNamesMode(false);
      } else {
        // Exiting measuring mode: clear range
        setRangeSourceId(null);
        emitRangeSource(null);
      }
      return next;
    });
  }, [emitRangeSource, gridConfig.type]);

  const togglePlayerControls = useCallback(async () => {
    if (!sessionToken) {
      toast.error('Player session is not ready yet.');
      return;
    }

    const next = !allowPlayerControls;
    setAllowPlayerControls(next);
    const syncEnabled = isGameplaySyncEnabledRef.current;
    const payload = next && currentScene
      ? (syncEnabled ? buildFullPlayerScenePayload(currentScene) : buildPlayerScenePayload(currentScene))
      : null;

    try {
      await updateSessionDoc(db, sessionToken, {
        allowPlayerControls: next,
        scene: next ? payload : undefined,
        gameplaySyncEnabled: syncEnabled,
      });
    } catch (error) {
      console.warn('Failed toggling player controls', error);
      setAllowPlayerControls((prev) => !next);
      toast.error('Failed to update player controls.');
    }
  }, [allowPlayerControls, currentScene, sessionToken]);

  const toggleBreak = useCallback(async () => {
    const next = !isOnBreak;
    setIsOnBreak(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('orchestratorBreak', next ? 'true' : 'false');
    }
    safePostMessage({ type: 'breakUpdate', isOnBreak: next });
    if (sessionToken) {
      try {
        await updateSessionDoc(db, sessionToken, { isOnBreak: next });
      } catch (error) {
        console.warn('Failed syncing break state to session', error);
        // Roll back local state on failure
        setIsOnBreak(!next);
        if (typeof window !== 'undefined') {
          localStorage.setItem('orchestratorBreak', !next ? 'true' : 'false');
        }
        safePostMessage({ type: 'breakUpdate', isOnBreak: !next });
        toast.error('Failed to update break state.');
      }
    }
  }, [isOnBreak, sessionToken]);

  // Sync range source with selected token while measuring mode is active
  useEffect(() => {
    if (gridConfig.type !== 'hex' || !measuringMode) return;
    setMeasuringMode(false);
    setRangeSourceId(null);
    emitRangeSource(null);
  }, [emitRangeSource, gridConfig.type, measuringMode]);

  useEffect(() => {
    if (!measuringMode) return;
    if (!selectedTokenId) {
      // Keep current range source when measuring was activated externally (e.g. player command).
      if (!rangeSourceId) {
        setMeasuringMode(false);
        emitRangeSource(null);
      }
      return;
    }
    if (rangeSourceId !== selectedTokenId) {
      setRangeSourceId(selectedTokenId);
      emitRangeSource(selectedTokenId);
    }
  }, [measuringMode, selectedTokenId, rangeSourceId, emitRangeSource]);

  useEffect(() => {
    if (!channelReady) return;
    safePostMessage({ type: 'showNamesMode', active: showNamesMode });
  }, [showNamesMode, channelReady, safePostMessage]);

  const sendCenterScroll = useCallback(() => {
    if (!currentScene) return false;
    updateGridViewState({ ...gmViewState, centerX: 0.5, centerY: 0.5 });
    return true;
  }, [currentScene, gmViewState, updateGridViewState]);

  const centerSelectedTokenOnDisplay = useCallback(() => {
    if (!selectedTokenId || !currentScene) return false;
    const token = (currentScene.tokens || []).find(
      (item) => item.id === selectedTokenId && item.visible !== false
    );
    if (!token) return false;

    const center = gridConfig.type === 'hex'
      ? hexCellToPercent(token.x ?? 0, token.y ?? 0, gridConfig.cols, gridConfig.rows)
      : {
          pctX: (((Number(token.x) || 0) + getGridUnitsForSize(token.size) / 2) / gridConfig.cols) * 100,
          pctY: (((Number(token.y) || 0) + getGridUnitsForSize(token.size) / 2) / gridConfig.rows) * 100,
        };
    updateGridViewState({ ...gmViewState, centerX: center.pctX / 100, centerY: center.pctY / 100 });
    return true;
  }, [selectedTokenId, currentScene, gridConfig, gmViewState, updateGridViewState]);

  const centerSelectedTokenRef = useRef(centerSelectedTokenOnDisplay);
  useEffect(() => {
    centerSelectedTokenRef.current = centerSelectedTokenOnDisplay;
  }, [centerSelectedTokenOnDisplay]);

  useEffect(() => {
    if (!autoCenterOnSelect) return;
    centerSelectedTokenRef.current();
  }, [selectedTokenId, autoCenterOnSelect]);

  const requestCenterScroll = useCallback(() => {
    if (sendCenterScroll()) {
      pendingCenterRef.current = false;
    } else {
      pendingCenterRef.current = true;
    }
  }, [sendCenterScroll]);

  useEffect(() => {
    if (!pendingCenterRef.current) return;
    if (sendCenterScroll()) {
      pendingCenterRef.current = false;
    }
  }, [sendCenterScroll]);

  useEffect(() => {
    if (!currentScene) {
      layerVisibilityRef.current = new Map();
      return;
    }
    const prevMap = layerVisibilityRef.current;
    const nextMap = new Map();
    let shouldCenter = false;
    (currentScene.layers || []).forEach((layer) => {
      const isVisible = layer.visible !== false;
      const wasVisible = prevMap.get(layer.id) ?? false;
      if (!shouldCenter && layer.centerOnShow && isVisible && !wasVisible) {
        shouldCenter = true;
      }
      nextMap.set(layer.id, isVisible);
    });
    layerVisibilityRef.current = nextMap;
    if (shouldCenter) {
      requestCenterScroll();
    }
  }, [currentScene, requestCenterScroll]);

  const openEffectPicker = () => setShowEffectModal(true);
  const cinematicModeActive = currentScene?.effect?.type === 'cinematicMode';

  const setEffect = useCallback((type) => {
    if (!currentScene) return;
    const updatedScene = { ...currentScene, effect: type ? { type } : null };
    persistScene(updatedScene);
  }, [currentScene, persistScene]);

  const readImportFile = useCallback(async () => {
    if (typeof window === 'undefined') return null;

    if (window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          types: [
            {
              description: 'Orchestrator Scene Import',
              accept: { 'application/json': ['.json'] },
            },
          ],
        });
        return await handle.getFile();
      } catch (error) {
        if (error && error.name === 'AbortError') return null;
        console.warn('File picker failed', error);
        return null;
      }
    }

    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = () => {
        const file = input.files && input.files[0];
        resolve(file || null);
      };
      input.click();
    });
  }, []);

  const buildSceneExport = useCallback(async (sceneToExport = currentScene) => {
    if (!sceneToExport) return null;
    const layers = await Promise.all(
      (sceneToExport.layers || []).map(async (layer) => {
        let imageData = layer.dataUrl || null;
        if (!imageData && layer.imageId) {
          try {
            imageData = await getImage(layer.imageId);
          } catch {
            imageData = null;
          }
        }
        return { ...layer, imageData };
      })
    );

    const tokens = await Promise.all(
      (sceneToExport.tokens || []).map(async (token) => {
        const goodId = token.goodImageId || token.imageId || null;
        const evilId = token.evilImageId || token.altImageId || null;
        const neutralId = token.neutralImageId || null;

        const [goodImageData, evilImageData, neutralImageData] = await Promise.all(
          [goodId, evilId, neutralId].map(async (id) => {
            if (!id) return null;
            try {
              return await getImage(id);
            } catch {
              return null;
            }
          })
        );

        return {
          ...token,
          goodImageData,
          evilImageData,
          neutralImageData,
        };
      })
    );

    const battleEffects = (sceneToExport.battleEffects || []).map((effect) => ({
      ...effect,
      cells: Array.isArray(effect.cells)
        ? effect.cells.map((cell) => ({
            x: Number.isFinite(cell.x) ? Number(cell.x) : 0,
            y: Number.isFinite(cell.y) ? Number(cell.y) : 0,
          }))
        : [],
    }));

    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      scene: {
        id: sceneToExport.id,
        effect: sceneToExport.effect || null,
        ttrpgSystem: sceneToExport.ttrpgSystem || null,
        layers,
        tokens,
        battleEffects,
      },
    };
  }, [currentScene]);

  const exportSceneToFile = useCallback(async (sceneToExport = currentScene) => {
    if (!sceneToExport) return;
    const payload = await buildSceneExport(sceneToExport);
    if (!payload) return;
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const suggestedName = `${(sceneToExport.name || 'scene')
      .replace(/[^a-z0-9-_]+/gi, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '') || 'scene'}_export.json`;

    const saveWithPicker = async () => {
      if (typeof window === 'undefined' || !window.showSaveFilePicker) return false;
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: 'Orchestrator Scene Export',
              accept: { 'application/json': ['.json'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      } catch (error) {
        if (error && error.name === 'AbortError') return true;
        console.warn('Failed to save via picker', error);
        return false;
      }
    };

    const saved = await saveWithPicker();
    if (saved) return;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = suggestedName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [buildSceneExport, currentScene]);

  const importSceneFromFile = useCallback(async () => {
    if (!currentScene) return;
    const file = await readImportFile();
    if (!file) return;

    setIsPreparingSession(true);

    try {
      let parsed;
      try {
        const text = await file.text();
        parsed = JSON.parse(text);
      } catch (error) {
        console.warn('Failed to parse import file', error);
        return;
      }

      const importedScene = parsed?.scene || parsed || null;
      if (!importedScene) return;

      const updatedScene = await processScenePayload(
        importedScene,
        currentScene.id,
        currentScene
      );

      persistScene(updatedScene);

      // If gameplay sync is enabled, trigger an immediate sync with upload
      if (isGameplaySyncEnabledRef.current && syncSceneToCloudRef.current) {
        syncSceneToCloudRef.current(updatedScene);
      }
    } catch (error) {
      console.warn('Scene import failed before completion', error);
      toast.error('Import failed before all assets loaded. Please retry.');
    } finally {
      setIsPreparingSession(false);
    }
  }, [currentScene, persistScene, readImportFile]);

  useEffect(() => {
    if (!channelRef.current) return;
    const channel = channelRef.current;
    const handleMessage = (event) => {
      const { data } = event;
      if (!data) return;
      if (data.type === 'displayViewport') {
        displayViewportRef.current = data.rect;
        displayViewportStore.set(data.rect);
      } else if (data.type === 'requestGridView') {
        channel.postMessage({
          type: 'gridViewState',
          sceneId: currentSceneId,
          viewState: gmViewStateRef.current,
        });
      } else if (data.type === 'requestScene' && currentScene) {
        channel.postMessage({
          type: 'sceneUpdate',
          sceneId: currentScene.id,
          scene: currentScene,
        });
        if (sessionToken) {
          channel.postMessage({ type: 'sessionUpdate', token: sessionToken });
        }
        channel.postMessage({ type: 'showNamesMode', active: showNamesMode });
        channel.postMessage({ type: 'trackersUpdate', trackers });
        channel.postMessage({ type: 'gridConfigUpdate', gridConfig });
        channel.postMessage({ type: 'dhTooltipTokenUpdate', tokenId: dhTooltipTokenId });
        // Re-send overlay state so display recovers after a reload
        setMapOverlayActive((active) => {
          if (active) {
            setWorldMap((wm) => {
              channel.postMessage({
                type: 'worldMapOverlay',
                active: true,
                worldMap: buildPlayerWorldMap(wm),
                view: mapOverlayViewRef.current,
              });
              return wm;
            });
          }
          return active;
        });
      } else if (
        data.type === 'sceneSelect' &&
        data.sceneId !== currentSceneId
      ) {
        setCurrentSceneId(data.sceneId);
      } else if (data.type === 'rangeSource') {
        setRangeSourceId(data.tokenId || null);
      } else if (data.type === 'requestDisplayRotation') {
        channel.postMessage({
          type: 'displayRotation',
          flipped: isDisplayReversed,
        });
      } else if (data.type === 'requestTrackers') {
        channel.postMessage({ type: 'trackersUpdate', trackers });
      } else if (data.type === 'rollComplete') {
        // The Display has finished the 3D animation and computed the actual result.
        // Now write to Firebase and show the GM toast with the correct values.
        const rollIdKey = String(data.rollId ?? '');
        const pending = pendingRollsRef.current.get(rollIdKey);
        if (!pending) return;
        pendingRollsRef.current.delete(rollIdKey);

        const token = sessionTokenRef.current;
        if (!token) return;

        const rollRecord = {
          ...pending.baseRecord,
          displayText: data.displayText || '',
          data: data.data || {},
        };

        (async () => {
          try {
            await writeSessionRoll(db, token, rollRecord);
          } catch (err) {
            console.warn('Failed persisting roll after animation', err);
          }
        })();

        const gmMessage = `${rollRecord.username}: ${rollRecord.displayText}`;
        const gmOptions = {
          style: {
            background: pending.theme.backgroundSoft,
            color: '#fff',
            border: `2px solid ${pending.theme.accent}`,
          },
        };
        toast(gmMessage, gmOptions);
      }
    };
    channel.onmessage = handleMessage;
    return () => {
      channel.onmessage = null;
    };
  }, [currentScene, currentSceneId, dhTooltipTokenId, displayViewportStore, gridConfig, isDisplayReversed, showNamesMode, trackers]);

  useEffect(() => {
    const scene = scenes.find((item) => item.id === currentSceneId);
    if (scene) {
      safePostMessage({
        type: 'sceneUpdate',
        sceneId: scene.id,
        scene,
      });
    }
  }, [currentSceneId, scenes]);

  useEffect(() => {
    if (!rangeSourceId || !currentScene) return;
    const exists = currentScene.tokens?.some(
      (token) => token.id === rangeSourceId && token.visible !== false
    );
    if (!exists) {
      setRangeSourceId(null);
      emitRangeSource(null);
    }
  }, [currentScene, emitRangeSource, rangeSourceId]);

  const openSceneModal = () => {
    setSceneForm({ name: '' });
    setShowSceneModal(true);
  };

  const handleGridConfigSave = (newConfig) => {
    setGridConfig(newConfig);
    if (currentScene) {
      persistScene({ ...currentScene, gridConfig: newConfig });
    }
    safePostMessage({ type: 'gridConfigUpdate', gridConfig: newConfig });
    setShowGridConfigModal(false);
  };

  const handleSystemButtonClick = () => {
    if (!currentScene) return;
    if (currentScene.ttrpgSystem) {
      setShowSystemDmScreenModal(true);
    } else {
      setShowSystemSelectModal(true);
    }
  };

  const handleSelectSystem = (systemId) => {
    if (!currentScene) return;
    const updated = { ...currentScene, ttrpgSystem: systemId };
    persistScene(updated);
  };

  // ── Tracker handlers ──────────────────────────────────────────────────────

  // When system changes to/from daggerheart, initialize or clear trackers
  useEffect(() => {
    trackersRef.current = trackers;
  }, [trackers]);

  useEffect(() => {
    const system = currentScene?.ttrpgSystem || null;
    if (system === 'daggerheart') {
      const prev = trackersRef.current;
      if (prev.some((t) => t.id === FEAR_TRACKER_ID)) return;

      const next = prev.length > 0
        ? [buildFearTracker(), ...prev]
        : getInitialTrackers('daggerheart');

      trackersRef.current = next;
      setTrackers(next);
      saveTrackers(next);
    } else {
      // Not daggerheart: clear trackers (don't persist – keep storage for when
      // user switches back to daggerheart)
      trackersRef.current = [];
      setTrackers([]);
    }
  }, [currentScene?.ttrpgSystem]);

  // Broadcast tracker updates to display
  const emitTrackers = useCallback(
    (updatedTrackers) => {
      safePostMessage({ type: 'trackersUpdate', trackers: updatedTrackers });
    },
    [safePostMessage]
  );

  // Send a tracker-toast command to the player display via BroadcastChannel
  const emitTrackerToast = useCallback(
    (action, tracker, changeInfo) => {
      safePostMessage({ type: 'trackerToast', action, tracker, changeInfo: changeInfo || null });
    },
    [safePostMessage]
  );

  // Show a red-border announcement toast on the GM screen (like dice roll toasts)
  const showTrackerAnnouncementLocally = useCallback((tracker, changeInfo) => {
    const toastId = TRACKER_ANNOUNCE_ID(tracker.id);
    const content = <TrackerToastContent tracker={tracker} changeInfo={changeInfo} variant='announcement' />;
    const options = {
      toastId,
      autoClose: 5000,
      style: TRACKER_ANNOUNCE_STYLE,
    };
    if (toast.isActive(toastId)) {
      toast.update(toastId, { render: content, autoClose: 5000 });
    } else {
      toast(content, options);
    }
  }, []);

  const updateTrackers = useCallback(
    (next) => {
      trackersRef.current = next;
      setTrackers(next);
      saveTrackers(next);
      emitTrackers(next);
    },
    [emitTrackers]
  );

  const handleChangeTrackerValue = useCallback(
    (trackerId, newValue) => {
      const prev = trackersRef.current;
      const oldTracker = prev.find((t) => t.id === trackerId);
      if (!oldTracker) return;

      const clampedValue = Math.max(0, Math.min(newValue, oldTracker.size));
      if (clampedValue === oldTracker.value) return;

      const next = prev.map((t) =>
        t.id === trackerId ? { ...t, value: clampedValue } : t
      );
      const updated = next.find((t) => t.id === trackerId);
      const changeInfo = { oldValue: oldTracker.value, newValue: clampedValue };
      const gmDisplayName = localStorage.getItem('displayName') || '';
      const gmLabel = gmDisplayName ? `GM (${gmDisplayName})` : 'Game Master';
      const trackerHistoryRecord = buildTrackerHistoryRecord(updated, changeInfo, gmLabel);

      updateTrackers(next);
      showTrackerAnnouncementLocally(updated, changeInfo);
      emitTrackerToast('update', updated, changeInfo);
      if (sessionToken && trackerHistoryRecord) {
        writeSessionRoll(db, sessionToken, trackerHistoryRecord).catch((error) => {
          console.warn('Failed persisting tracker history event', error);
        });
      }
    },
    [sessionToken, showTrackerAnnouncementLocally, emitTrackerToast, updateTrackers]
  );

  const handleToggleTrackerVisible = useCallback(
    (trackerId) => {
      const prev = trackersRef.current;
      const next = prev.map((t) =>
        t.id === trackerId ? { ...t, visible: !t.visible } : t
      );
      const updated = next.find((t) => t.id === trackerId);
      if (!updated) return;

      updateTrackers(next);
      if (updated.visible) {
        emitTrackerToast('show', updated, null);
      } else {
        emitTrackerToast('dismiss', { id: trackerId }, null);
      }
    },
    [emitTrackerToast, updateTrackers]
  );

  const handleReorderTrackers = useCallback(
    (newOrder) => {
      updateTrackers(newOrder);
    },
    [updateTrackers]
  );

  const openAddTracker = useCallback(() => {
    setTrackerForm({ id: null, name: '', size: 12, color: '#e94f37', group: '' });
    setIsEditingTracker(false);
    setShowTrackerModal(true);
  }, []);

  const openEditTracker = useCallback((tracker) => {
    setTrackerForm({ id: tracker.id, name: tracker.name, size: tracker.size, color: tracker.color || '#e94f37', group: tracker.group || '' });
    setIsEditingTracker(true);
    setShowTrackerModal(true);
  }, []);

  const saveTrackerFromModal = useCallback(() => {
    const name = (trackerForm.name || '').trim();
    if (!name) return;
    const size = trackerForm.size || 12;
    const color = trackerForm.color || '#e94f37';
    const group = (trackerForm.group || '').trim();
    const prev = trackersRef.current;

    if (isEditingTracker && trackerForm.id) {
      const next = prev.map((t) =>
        t.id === trackerForm.id
          ? { ...t, name, size, color, group, value: Math.min(t.value, size) }
          : t
      );
      updateTrackers(next);
    } else {
      const newTracker = {
        id: `tracker_${Date.now()}`,
        name,
        size,
        color,
        group,
        value: 0,
        visible: true,
        locked: false,
      };
      const next = [...prev, newTracker];
      updateTrackers(next);
    }

    setShowTrackerModal(false);
  }, [trackerForm, isEditingTracker, updateTrackers]);

  const handleDeleteTracker = useCallback(
    (trackerId) => {
      const prev = trackersRef.current;
      const next = prev.filter((t) => t.id !== trackerId);
      if (next.length === prev.length) return;
      updateTrackers(next);
    },
    [updateTrackers]
  );

  const toggleGroupTrackersVisible = useCallback(
    (groupName) => {
      const prev = trackersRef.current;
      const groupTrackers = prev.filter((t) => !t.locked && (t.group || '').trim() === groupName);
      const allVisible = groupTrackers.every((t) => t.visible !== false);
      const next = prev.map((t) =>
        !t.locked && (t.group || '').trim() === groupName
          ? { ...t, visible: !allVisible }
          : t
      );
      updateTrackers(next);
    },
    [updateTrackers]
  );

  // ── End tracker handlers ──────────────────────────────────────────────────

  const saveSceneFromModal = () => {
    const name = (sceneForm.name || '').trim();
    if (!name) return;
    const id = `scene_${Date.now()}`;
    // Pre-select a system if all existing scenes share the same one
    const existingSystems = scenes.map((s) => s.ttrpgSystem || null);
    const allSame =
      existingSystems.length > 0 &&
      existingSystems.every((sys) => sys === existingSystems[0]);
    const preselectedSystem = allSame ? existingSystems[0] : null;
    const scene = {
      id,
      name,
      layers: [],
      tokens: [],
      battleEffects: [],
      thumb: null,
      ttrpgSystem: preselectedSystem,
    };
    setScenes((prev) => {
      const updated = [...prev, scene];
      saveScenes(updated);
      return updated;
    });
    setCurrentSceneId(id);
    setShowSceneModal(false);
    safePostMessage({
      type: 'sceneUpdate',
      sceneId: id,
      scene,
    });
    // Sync new (empty) scene to cloud
    if (isGameplaySyncEnabledRef.current && syncSceneToCloudRef.current) {
      syncSceneToCloudRef.current(scene);
    }
  };

  const askDeleteScene = (sceneId) => {
    setPendingDeleteId(sceneId);
    setShowDeleteSceneModal(true);
  };

  const confirmDeleteScene = () => {
    const deletedId = pendingDeleteId;
    setScenes((prev) => {
      const updated = prev.filter((scene) => scene.id !== deletedId);
      if (currentSceneId === deletedId) {
        const nextSceneId = updated.length ? updated[0].id : null;
        setCurrentSceneId(nextSceneId);
      }
      saveScenes(updated);
      return updated;
    });
    setShowDeleteSceneModal(false);
    setPendingDeleteId(null);
    // Delete from cloud if sync enabled
    if (isGameplaySyncEnabledRef.current && deletedId) {
      const username = localStorage.getItem('username');
      if (username) {
        deleteSceneGameplay(db, username, deletedId).catch((err) => {
          console.warn('[GameplaySync] Failed deleting scene from cloud', err);
        });
      }
    }
  };

  const cancelDeleteScene = () => {
    setShowDeleteSceneModal(false);
    setPendingDeleteId(null);
  };

  const pendingDeleteScene = scenes.find((scene) => scene.id === pendingDeleteId) || null;

  const updateTokenForm = (updates) => {
    setTokenForm((prev) => ({ ...prev, ...updates }));
  };

  const updateLayerForm = (updates) => {
    setLayerForm((prev) => ({ ...prev, ...updates }));
  };

  const updateSceneForm = (updates) => {
    setSceneForm((prev) => ({ ...prev, ...updates }));
  };

  const updateBattleEffectForm = (updates) => {
    setBattleEffectForm((prev) => ({ ...prev, ...updates }));
  };

  const openAddBattleEffect = () => {
    setIsEditingBattleEffect(false);
    setBattleEffectForm(buildBattleEffectDefaults());
    setShowBattleEffectModal(true);
  };

  const openEditBattleEffect = (effect) => {
    const fallback = buildBattleEffectDefaults(effect?.kind || 'fog');
    setIsEditingBattleEffect(true);
    setBattleEffectForm({
      id: effect.id,
      name: effect.name || fallback.name,
      kind: effect.kind || 'fog',
      color: effect.color || fallback.color,
      opacity: clampEffectOpacity(effect.opacity ?? fallback.opacity),
      visible: effect.visible !== false,
      group: effect.group || '',
      shape: effect.shape || '',
      size: clampEffectSize(effect.size ?? 3),
      cells: Array.isArray(effect.cells) ? effect.cells : [],
    });
    setShowBattleEffectModal(true);
  };

  const saveBattleEffectFromModal = () => {
    if (!currentScene) return;
    const form = { ...battleEffectForm };
    const base = buildBattleEffectDefaults(form.kind || 'fog');
    const shape = VALID_EFFECT_SHAPES.includes(form.shape) ? form.shape : '';
    const size = clampEffectSize(form.size ?? 3);

    let cells;
    if (shape) {
      // Auto-generate cells from the shape. If the effect already has cells, derive the
      // centre from their average position; otherwise default to the centre of the grid.
      const existingEffect = shape && isEditingBattleEffect
        ? (currentScene.battleEffects || []).find((e) => e.id === form.id)
        : null;
      let cx = Math.floor(gridConfig.cols / 2);
      let cy = Math.floor(gridConfig.rows / 2);
      if (existingEffect && Array.isArray(existingEffect.cells) && existingEffect.cells.length > 0) {
        const xs = existingEffect.cells.map((c) => Number(c.x));
        const ys = existingEffect.cells.map((c) => Number(c.y));
        cx = Math.round(xs.reduce((a, b) => a + b, 0) / xs.length);
        cy = Math.round(ys.reduce((a, b) => a + b, 0) / ys.length);
      }
      cells = generateShapeCells(shape, size, cx, cy);
    } else {
      cells = Array.isArray(form.cells)
        ? form.cells
            .map((cell) => ({
              x: Math.min(gridConfig.cols - 1, Math.max(0, Math.round(Number(cell.x) || 0))),
              y: Math.min(gridConfig.rows - 1, Math.max(0, Math.round(Number(cell.y) || 0))),
            }))
            .filter((cell) => Number.isFinite(cell.x) && Number.isFinite(cell.y))
        : [];
    }

    const effectPayload = {
      id: form.id || `bfx_${Date.now()}`,
      name: (form.name || base.name || 'Effect').trim(),
      kind: form.kind || 'fog',
      color: form.color || base.color,
      opacity: clampEffectOpacity(form.opacity ?? base.opacity),
      visible: form.visible !== false,
      group: form.group || '',
      shape,
      size,
      cells,
    };

    const updatedScene = { ...currentScene };
    const existing = Array.isArray(updatedScene.battleEffects) ? updatedScene.battleEffects : [];
    if (isEditingBattleEffect && form.id) {
      updatedScene.battleEffects = existing.map((effect) =>
        effect.id === form.id ? { ...effect, ...effectPayload } : effect
      );
    } else {
      updatedScene.battleEffects = [...existing, effectPayload];
      // Only enter painting mode when no shape was provided (manual painting workflow).
      if (!shape) {
        setPaintingBattleEffectId(effectPayload.id);
      }
    }

    persistScene(updatedScene);
    setShowBattleEffectModal(false);
  };

  const deleteBattleEffect = (id) => {
    if (!currentScene) return;
    const updatedScene = {
      ...currentScene,
      battleEffects: (currentScene.battleEffects || []).filter(
        (effect) => effect && effect.id !== id
      ),
    };
    persistScene(updatedScene);
    if (paintingBattleEffectId === id) {
      setPaintingBattleEffectId(null);
    }
  };

  const toggleBattleEffectVisible = (id) => {
    if (!currentScene) return;
    const updatedScene = {
      ...currentScene,
      battleEffects: (currentScene.battleEffects || []).map((effect) =>
        effect.id === id ? { ...effect, visible: effect.visible === false ? true : false } : effect
      ),
    };
    persistScene(updatedScene);
  };

  const togglePaintBattleEffect = (id) => {
    setPaintingBattleEffectId((prev) => (prev === id ? null : id));
  };

  const paintBattleEffectCells = useCallback(
    (effectId, changes) => {
      if (!currentScene || !effectId) return;
      const updatedScene = {
        ...currentScene,
        battleEffects: (currentScene.battleEffects || []).map((effect) => {
          if (effect.id !== effectId) return effect;
          return {
            ...effect,
            cells: applyBattleEffectCellChanges(
              effect.cells || [],
              changes,
              gridConfig.cols,
              gridConfig.rows
            ),
          };
        }),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene, gridConfig]
  );

  const handleTokenRangeToggle = useCallback(
    (tokenId) => {
      setRangeSourceId((prev) => {
        const next = prev === tokenId ? null : tokenId;
        emitRangeSource(next);
        setMeasuringMode(next !== null);
        if (next) {
          // Measuring mode takes precedence over names mode when activated via double-click.
          setShowNamesMode(false);
        }
        return next;
      });
    },
    [emitRangeSource]
  );

  const openAddToken = () => {
    setIsEditingToken(false);
    setTokenForm({
      id: null,
      name: '',
      group: '',
      grayscale: false,
      visible: true,
      goodPreview: null,
      evilPreview: null,
      neutralPreview: null,
      goodFile: null,
      evilFile: null,
      neutralFile: null,
      goodImageId: null,
      evilImageId: null,
      neutralImageId: null,
      disposition: 'good',
      showInitial: true,
      size: 1,
      conditions: [],
      dhHpMax: 0,
      dhHpUsed: 0,
      dhStressMax: 0,
      dhStressUsed: 0,
      dhExtra1Label: '',
      dhExtra1Max: 0,
      dhExtra1Used: 0,
      dhExtra2Label: '',
      dhExtra2Max: 0,
      dhExtra2Used: 0,
      dhThresholdMajor: 0,
      dhThresholdSevere: 0,
    });
    setShowTokenModal(true);
  };

  const openEditToken = useCallback(async (token) => {
    let goodPreview = null;
    let evilPreview = null;
    let neutralPreview = null;

    const goodId = token.goodImageId || token.imageId || null;
    const evilId = token.evilImageId || token.altImageId || null;
    const neutralId = token.neutralImageId || null;

    if (goodId) {
      try {
        goodPreview = await getImage(goodId);
      } catch {
        goodPreview = null;
      }
    }
    if (evilId) {
      try {
        evilPreview = await getImage(evilId);
      } catch {
        evilPreview = null;
      }
    }
    if (neutralId) {
      try {
        neutralPreview = await getImage(neutralId);
      } catch {
        neutralPreview = null;
      }
    }
    setIsEditingToken(true);
    setTokenForm({
      id: token.id,
      name: token.name || '',
      group: token.group || '',
      grayscale: !!token.grayscale,
      visible: token.visible !== false,
      goodPreview,
      evilPreview,
      neutralPreview,
      goodFile: null,
      evilFile: null,
      neutralFile: null,
      goodImageId: goodId,
      evilImageId: evilId,
      neutralImageId: neutralId,
      disposition: token.disposition || 'good',
      showInitial: token.showInitial !== false,
      size: getStoredSizeValue(token.size),
      conditions: Array.isArray(token.conditions) ? token.conditions : [],
      dhHpMax: token.dhHpMax ?? 0,
      dhHpUsed: token.dhHpUsed ?? 0,
      dhStressMax: token.dhStressMax ?? 0,
      dhStressUsed: token.dhStressUsed ?? 0,
      dhExtra1Label: token.dhExtra1Label ?? '',
      dhExtra1Max: token.dhExtra1Max ?? 0,
      dhExtra1Used: token.dhExtra1Used ?? 0,
      dhExtra2Label: token.dhExtra2Label ?? '',
      dhExtra2Max: token.dhExtra2Max ?? 0,
      dhExtra2Used: token.dhExtra2Used ?? 0,
      dhThresholdMajor: token.dhThresholdMajor ?? 0,
      dhThresholdSevere: token.dhThresholdSevere ?? 0,
    });
    setShowTokenModal(true);
  }, []);

  const saveTokenFromModal = useCallback(async () => {
    if (!currentScene) return;
    const updatedScene = { ...currentScene };
    const form = { ...tokenForm };
    let goodImageId = form.goodImageId || null;
    let evilImageId = form.evilImageId || null;
    let neutralImageId = form.neutralImageId || null;
    const normalizedSize = getStoredSizeValue(form.size);
    if (form.goodFile && form.goodPreview) {
      try {
        goodImageId = await storeImage(form.goodPreview);
      } catch {
        goodImageId = form.goodImageId || null;
      }
    }
    if (form.evilFile && form.evilPreview) {
      try {
        evilImageId = await storeImage(form.evilPreview);
      }
      catch {
        evilImageId = form.evilImageId || null;
      }
    }
    if (form.neutralFile && form.neutralPreview) {
      try {
        neutralImageId = await storeImage(form.neutralPreview);
      }
      catch {
        neutralImageId = form.neutralImageId || null;
      }
    }

    if (isEditingToken && form.id) {
      updatedScene.tokens = updatedScene.tokens.map((token) =>
        token.id === form.id
          ? {
              ...token,
              name: form.name,
              group: form.group || '',
              grayscale: !!form.grayscale,
              visible: form.visible !== false,
              goodImageId,
              evilImageId,
              neutralImageId,
              disposition: form.disposition || 'good',
              showInitial: form.showInitial !== false,
              size: normalizedSize,
              conditions: Array.isArray(form.conditions) ? form.conditions : [],
              dhHpMax: form.dhHpMax ?? 0,
              dhHpUsed: form.dhHpUsed ?? 0,
              dhStressMax: form.dhStressMax ?? 0,
              dhStressUsed: form.dhStressUsed ?? 0,
              dhExtra1Label: form.dhExtra1Label ?? '',
              dhExtra1Max: form.dhExtra1Max ?? 0,
              dhExtra1Used: form.dhExtra1Used ?? 0,
              dhExtra2Label: form.dhExtra2Label ?? '',
              dhExtra2Max: form.dhExtra2Max ?? 0,
              dhExtra2Used: form.dhExtra2Used ?? 0,
              dhThresholdMajor: form.dhThresholdMajor ?? 0,
              dhThresholdSevere: form.dhThresholdSevere ?? 0,
            }
          : token
      );
    } else {
      const id = `tok_${Date.now()}`;
      const newToken = {
        id,
        name: form.name || 'Token',
        group: form.group || '',
        grayscale: !!form.grayscale,
        visible: form.visible !== false,
        goodImageId,
        evilImageId,
        neutralImageId,
        disposition: form.disposition || 'good',
        showInitial: form.showInitial !== false,
        size: normalizedSize,
        conditions: Array.isArray(form.conditions) ? form.conditions : [],
        dhHpMax: form.dhHpMax ?? 0,
        dhHpUsed: form.dhHpUsed ?? 0,
        dhStressMax: form.dhStressMax ?? 0,
        dhStressUsed: form.dhStressUsed ?? 0,
        dhExtra1Label: form.dhExtra1Label ?? '',
        dhExtra1Max: form.dhExtra1Max ?? 0,
        dhExtra1Used: form.dhExtra1Used ?? 0,
        dhExtra2Label: form.dhExtra2Label ?? '',
        dhExtra2Max: form.dhExtra2Max ?? 0,
        dhExtra2Used: form.dhExtra2Used ?? 0,
        dhThresholdMajor: form.dhThresholdMajor ?? 0,
        dhThresholdSevere: form.dhThresholdSevere ?? 0,
      };
      const { placedTokens, unplacedTokens } = placeTokensAtBottomRight(
        updatedScene.tokens || [],
        [newToken],
        gridConfig
      );
      if (unplacedTokens.length) {
        toast.warn('No free space is available in the bottom-right area of this grid.');
        return;
      }
      updatedScene.tokens = [...(updatedScene.tokens || []), ...placedTokens];
    }

    persistScene(updatedScene);
    setShowTokenModal(false);
  }, [currentScene, tokenForm, isEditingToken, persistScene]);

  const duplicateTokenFromModal = useCallback((count) => {
    if (!currentScene || !isEditingToken || !tokenForm.id) return;
    const source = currentScene.tokens.find((t) => t.id === tokenForm.id);
    if (!source) return;
    const copies = Math.max(1, Math.floor(Number(count) || 1));
    const base = Date.now();
    const names = getDuplicateTokenNames(source.name || 'Token', currentScene.tokens, copies);
    const newTokens = Array.from({ length: copies }, (_, index) => ({
      ...source,
      id: `tok_${base}_dup_${index}_${Math.random().toString(16).slice(2, 8)}`,
      name: names[index],
    }));
    const { placedTokens, unplacedTokens } = placeTokensAtBottomRight(
      currentScene.tokens || [],
      newTokens,
      gridConfig
    );
    const updatedScene = {
      ...currentScene,
      tokens: [...(currentScene.tokens || []), ...placedTokens],
    };
    if (placedTokens.length) persistScene(updatedScene);
    if (unplacedTokens.length) {
      toast.warn(`${unplacedTokens.length} duplicate token${unplacedTokens.length === 1 ? '' : 's'} could not fit in the bottom-right area.`);
    }
    setShowTokenModal(false);
  }, [currentScene, gridConfig, isEditingToken, tokenForm.id, persistScene]);

  const handleSaveEditedScenes = useCallback((editedScenes) => {
    setScenes((prev) => {
      // Apply new names and new order from editedScenes
      const idToName = new Map(editedScenes.map((s) => [s.id, s.name]));
      const orderedIds = editedScenes.map((s) => s.id);
      const sceneMap = new Map(prev.map((s) => [s.id, s]));
      const updated = orderedIds
        .filter((id) => sceneMap.has(id))
        .map((id) => ({ ...sceneMap.get(id), name: idToName.get(id) || sceneMap.get(id).name }));
      saveScenes(updated);
      // If currentSceneId was removed, switch to first available
      if (!updated.some((s) => s.id === currentSceneId) && updated.length > 0) {
        setCurrentSceneId(updated[0].id);
      }
      return updated;
    });
    setShowEditScenesModal(false);
  }, [currentSceneId]);

  const deleteTokenFromModal = useCallback(() => {
    if (!currentScene || !isEditingToken || !tokenForm.id) return;
    const updatedScene = {
      ...currentScene,
      tokens: currentScene.tokens.filter((token) => token.id !== tokenForm.id),
    };
    persistScene(updatedScene);
    setShowTokenModal(false);
  }, [currentScene, isEditingToken, tokenForm.id, persistScene]);

  const importTokensFromScenes = useCallback(
    (tokensToImport) => {
      if (!currentScene || !tokensToImport || !tokensToImport.length) {
        setShowLoadTokensModal(false);
        return;
      }
      const base = Date.now();
      const newTokens = tokensToImport.map(({ token }, index) => ({
        ...token,
        id: `tok_${base}_${index}_${Math.random().toString(16).slice(2, 8)}`,
      }));
      const { placedTokens, unplacedTokens } = placeTokensAtBottomRight(
        currentScene.tokens || [],
        newTokens,
        gridConfig
      );
      if (placedTokens.length) {
        persistScene({
          ...currentScene,
          tokens: [...(currentScene.tokens || []), ...placedTokens],
        });
      }
      if (unplacedTokens.length) {
        toast.warn(`${unplacedTokens.length} imported token${unplacedTokens.length === 1 ? '' : 's'} could not fit in the bottom-right area.`);
      }
      setShowLoadTokensModal(false);
    },
    [currentScene, gridConfig, persistScene]
  );

  const toggleTokenVisible = useCallback(
    (id) => {
      if (!currentScene) return;
      const updatedScene = {
        ...currentScene,
        tokens: currentScene.tokens.map((token) =>
          token.id === id
            ? { ...token, visible: token.visible === false ? true : false }
            : token
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleTokenDeath = useCallback(
    (id) => {
      if (!currentScene) return;
      const updatedScene = {
        ...currentScene,
        tokens: currentScene.tokens.map((token) =>
          token.id === id ? { ...token, grayscale: !token.grayscale } : token
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleTokenDisposition = useCallback(
    (id) => {
      if (!currentScene) return;
      const target = currentScene.tokens.find((t) => t.id === id);
      if (!target) return;
      const hasGood = !!(target.goodImageId || target.imageId);
      const hasEvil = !!(target.evilImageId || target.altImageId);
      if (!hasGood || !hasEvil) return; // only toggle when both images exist
      const updatedScene = {
        ...currentScene,
        tokens: currentScene.tokens.map((token) =>
          token.id === id
            ? {
                ...token,
                disposition: token.disposition === 'evil' ? 'good' : 'evil',
              }
            : token
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleLayerVisible = useCallback(
    (id) => {
      if (!currentScene) return;
      const updatedScene = {
        ...currentScene,
        layers: currentScene.layers.map((layer) =>
          layer.id === id ? { ...layer, visible: !layer.visible } : layer
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleGroupTokensVisible = useCallback(
    (groupName) => {
      if (!currentScene) return;
      const groupTokens = currentScene.tokens.filter(
        (t) => (t.group || '').trim() === groupName
      );
      const allVisible = groupTokens.every((t) => t.visible !== false);
      const updatedScene = {
        ...currentScene,
        tokens: currentScene.tokens.map((token) =>
          (token.group || '').trim() === groupName
            ? { ...token, visible: !allVisible }
            : token
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleGroupEffectsVisible = useCallback(
    (groupName) => {
      if (!currentScene) return;
      const groupEffects = (currentScene.battleEffects || []).filter(
        (e) => (e.group || '').trim() === groupName
      );
      const allVisible = groupEffects.every((e) => e.visible !== false);
      const updatedScene = {
        ...currentScene,
        battleEffects: (currentScene.battleEffects || []).map((effect) =>
          (effect.group || '').trim() === groupName
            ? { ...effect, visible: !allVisible }
            : effect
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const toggleGroupLayersVisible = useCallback(
    (groupName) => {
      if (!currentScene) return;
      const groupLayers = currentScene.layers.filter(
        (l) => (l.group || '').trim() === groupName
      );
      const allVisible = groupLayers.every((l) => l.visible !== false);
      const updatedScene = {
        ...currentScene,
        layers: currentScene.layers.map((layer) =>
          (layer.group || '').trim() === groupName
            ? { ...layer, visible: !allVisible }
            : layer
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const startLayerFlip = useCallback((groupName) => {
    if (layerFlipState?.groupName === groupName) {
      setLayerFlipState(null);
      return;
    }
    if (!currentScene) return;
    const groupLayers = (currentScene.layers || [])
      .filter((layer) => (layer.group || '').trim() === groupName)
      .slice()
      .sort((a, b) => (Number(a.z) || 0) - (Number(b.z) || 0));
    if (!groupLayers.length) return;

    const visibleIndex = groupLayers.findIndex((layer) => layer.visible !== false);
    const activeIndex = visibleIndex >= 0 ? visibleIndex : 0;
    const activeLayerId = groupLayers[activeIndex].id;
    setSelectedTokenId(null);
    setLayerFlipState({
      groupName,
      layerIds: groupLayers.map((layer) => layer.id),
      activeIndex,
    });
    persistScene({
      ...currentScene,
      layers: (currentScene.layers || []).map((layer) =>
        (layer.group || '').trim() === groupName
          ? { ...layer, visible: layer.id === activeLayerId }
          : layer
      ),
    });
  }, [currentScene, layerFlipState, persistScene]);

  const stepLayerFlip = useCallback((direction) => {
    if (!layerFlipState || !currentScene) return;
    const nextIndex = layerFlipState.activeIndex + direction;
    if (nextIndex < 0) {
      setLayerFlipState(null);
      return;
    }
    if (nextIndex >= layerFlipState.layerIds.length) {
      setLayerFlipState(null);
      return;
    }

    const activeLayerId = layerFlipState.layerIds[nextIndex];
    persistScene({
      ...currentScene,
      layers: (currentScene.layers || []).map((layer) =>
        (layer.group || '').trim() === layerFlipState.groupName
          ? { ...layer, visible: layer.id === activeLayerId }
          : layer
      ),
    });
    setLayerFlipState({ ...layerFlipState, activeIndex: nextIndex });
  }, [currentScene, layerFlipState, persistScene]);

  useEffect(() => {
    if (!layerFlipState) return undefined;
    const handleKeyDown = (event) => {
      const tag = event.target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        stepLayerFlip(-1);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        stepLayerFlip(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [layerFlipState, stepLayerFlip]);

  const reorderTokens = useCallback(
    (newTokens) => {
      if (!currentScene) return;
      persistScene({ ...currentScene, tokens: newTokens });
    },
    [currentScene, persistScene]
  );

  const reorderEffects = useCallback(
    (newEffects) => {
      if (!currentScene) return;
      persistScene({ ...currentScene, battleEffects: newEffects });
    },
    [currentScene, persistScene]
  );

  const reorderLayers = useCallback(
    (newLayers) => {
      if (!currentScene) return;
      persistScene({ ...currentScene, layers: newLayers });
    },
    [currentScene, persistScene]
  );

  const openAddLayer = () => {
    const nextZ =
      (currentScene?.layers?.reduce(
        (max, layer) => Math.max(max, layer.z),
        0
      ) || 0) + 1;
    setIsEditingLayer(false);
    setLayerForm({
      id: null,
      name: '',
      group: '',
      type: 'image',
      z: nextZ,
      bgColor: '',
      preview: null,
      file: null,
      imageId: null,
      centerOnShow: false,
      text: '',
      font: 'sans-serif',
      fontSize: 36,
      color: '#ffffff',
      drawColor: '#e94f37',
      strokeWidth: 4,
      paths: [],
    });
    setShowLayerModal(true);
  };

  const openEditLayer = useCallback(async (layer) => {
    let preview = null;
    if (layer.imageId) {
      try {
        preview = await getImage(layer.imageId);
      } catch {
        preview = null;
      }
    }
    setIsEditingLayer(true);
    setLayerForm({
      id: layer.id,
      name: layer.name || '',
      group: layer.group || '',
      type: layer.type || 'image',
      z: layer.z || 1,
      bgColor: layer.bgColor || '',
      preview,
      file: null,
      imageId: layer.imageId || null,
      centerOnShow: !!layer.centerOnShow,
      // text layer fields
      text: layer.text || '',
      font: layer.font || 'sans-serif',
      fontSize: layer.fontSize || 36,
      color: layer.color || '#ffffff',
      // draw layer fields
      drawColor: layer.drawColor || '#e94f37',
      strokeWidth: layer.strokeWidth || 4,
      paths: Array.isArray(layer.paths) ? layer.paths : [],
    });
    setShowLayerModal(true);
  }, []);

  const saveLayerFromModal = useCallback(async () => {
    if (!currentScene) return;
    const updatedScene = { ...currentScene };
    const form = { ...layerForm };
    const layerType = form.type || 'image';

    // Image-type: store image if changed
    let imageId = form.imageId || null;
    if (layerType === 'image' && form.file && form.preview) {
      try {
        imageId = await storeImage(form.preview);
      } catch {
        imageId = form.imageId || null;
      }
    }

    if (isEditingLayer && form.id) {
      updatedScene.layers = updatedScene.layers.map((layer) => {
        if (layer.id !== form.id) return layer;
        const base = {
          ...layer,
          name: form.name,
          group: form.group || '',
          z: Number(form.z) || 0,
          visible: layer.visible !== false,
        };
        if (layerType === 'image') {
          return {
            ...base,
            bgColor: form.bgColor || '',
            imageId,
            centerOnShow: !!form.centerOnShow,
          };
        }
        if (layerType === 'text') {
          return {
            ...base,
            text: form.text || '',
            font: form.font || 'sans-serif',
            fontSize: Number(form.fontSize) || 36,
            color: form.color || '#ffffff',
          };
        }
        if (layerType === 'draw') {
          return {
            ...base,
            drawColor: form.drawColor || '#e94f37',
            strokeWidth: Number(form.strokeWidth) || 4,
            paths: Array.isArray(form.paths) ? form.paths : [],
          };
        }
        return base;
      });
    } else {
      const id = `layer_${Date.now()}`;
      const base = {
        id,
        name: form.name || 'Layer',
        group: form.group || '',
        z: Number(form.z) || 0,
        type: layerType,
        visible: true,
      };
      let newLayer;
      if (layerType === 'image') {
        newLayer = {
          ...base,
          bgColor: form.bgColor || '',
          imageId,
          disposition: 'good',
          centerOnShow: !!form.centerOnShow,
        };
      } else if (layerType === 'text') {
        newLayer = {
          ...base,
          text: form.text || 'Text',
          font: form.font || 'sans-serif',
          fontSize: Number(form.fontSize) || 36,
          color: form.color || '#ffffff',
          x: 50,
          y: 50,
        };
      } else {
        // draw
        newLayer = {
          ...base,
          drawColor: form.drawColor || '#e94f37',
          strokeWidth: Number(form.strokeWidth) || 4,
          paths: [],
        };
      }
      updatedScene.layers = [...updatedScene.layers, newLayer];
    }

    persistScene(updatedScene);
    setShowLayerModal(false);
  }, [currentScene, layerForm, isEditingLayer, persistScene]);

  const addMassImagesFromModal = useCallback(async (files, group) => {
    if (!currentScene || !files.length || !group) return;
    const sortedFiles = files
      .slice()
      .sort((a, b) => compareImageFileNames(a.name, b.name));
    const nextZ = (currentScene.layers || []).reduce(
      (max, layer) => Math.max(max, Number(layer.z) || 0),
      0
    ) + 1;
    const newLayers = [];

    for (const [index, file] of sortedFiles.entries()) {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Image read failed'));
        reader.readAsDataURL(file);
      });
      const resized = await resizeImage(dataUrl, 2560, Infinity);
      const imageId = await storeImage(resized);
      const name = file.name.replace(/\.[^.]+$/, '') || file.name;

      newLayers.push({
        id: `layer_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name,
        group,
        z: nextZ + index,
        type: 'image',
        visible: true,
        bgColor: '',
        imageId,
        disposition: 'good',
        centerOnShow: false,
      });
    }

    persistScene({
      ...currentScene,
      layers: [...(currentScene.layers || []), ...newLayers],
    });
    setShowLayerModal(false);
  }, [currentScene, persistScene]);

  const deleteLayerFromModal = useCallback(() => {
    if (!currentScene || !isEditingLayer || !layerForm.id) return;
    const updatedScene = {
      ...currentScene,
      layers: currentScene.layers.filter((layer) => layer.id !== layerForm.id),
    };
    persistScene(updatedScene);
    setShowLayerModal(false);
  }, [currentScene, isEditingLayer, layerForm.id, persistScene]);

  const toggleDrawLayer = useCallback((id) => {
    setDrawingLayerId((prev) => (prev === id ? null : id));
  }, []);

  const addStrokeToDrawLayer = useCallback(
    (layerId, stroke) => {
      if (!currentScene) return;
      const updatedScene = {
        ...currentScene,
        layers: currentScene.layers.map((layer) =>
          layer.id === layerId
            ? { ...layer, paths: [...(layer.paths || []), stroke] }
            : layer
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const moveTextLayer = useCallback(
    (layerId, x, y) => {
      if (!currentScene) return;
      const updatedScene = {
        ...currentScene,
        layers: currentScene.layers.map((layer) =>
          layer.id === layerId ? { ...layer, x, y } : layer
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene]
  );

  const onDragToken = useCallback(
    (id, worldPoint) => {
      if (!currentScene) return;
      const token = currentScene.tokens.find((item) => item.id === id);
      if (!token || !worldPoint) return;
      const position = getTokenGridPositionFromWorldPoint(
        worldPoint,
        getGridUnitsForSize(token.size),
        gridConfig
      );
      if (!position) return;
      const { x, y } = position;

      if (token.x === x && token.y === y) return;
      const updatedScene = {
        ...currentScene,
        tokens: currentScene.tokens.map((token) =>
          token.id === id ? { ...token, x, y } : token
        ),
      };
      persistScene(updatedScene);
    },
    [currentScene, persistScene, gridConfig]
  );

  const onNudgeToken = useCallback(
    (id, dx, dy) => {
      if (!currentScene) return;
      const updatedScene = nudgeTokenOnScene(
        currentScene,
        id,
        dx,
        dy,
        gridConfig.cols,
        gridConfig.rows,
        gridConfig.type
      );
      if (!updatedScene) return;
      persistScene(updatedScene);
    },
    [currentScene, persistScene, gridConfig]
  );

  // Keyboard arrow key support for nudging the selected token
  useEffect(() => {
    const handler = (e) => {
      if (!selectedTokenId || layerFlipState) return;
      const tag = e.target && e.target.tagName ? String(e.target.tagName).toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      let dx = 0;
      let dy = 0;
      switch (e.key) {
        case 'ArrowUp':
          dy = -1;
          break;
        case 'ArrowDown':
          dy = 1;
          break;
        case 'ArrowLeft':
          dx = -1;
          break;
        case 'ArrowRight':
          dx = 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      onNudgeToken(selectedTokenId, dx, dy);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedTokenId, layerFlipState, onNudgeToken]);

  // Handle token selection – show Daggerheart tooltip when applicable
  const handleSelectToken = useCallback((tokenId) => {
    if (tokenId) setLayerFlipState(null);
    setSelectedTokenId(tokenId);
    const isDaggerheart = currentScene?.ttrpgSystem === 'daggerheart';
    const tooltipTokenId = isDaggerheart ? tokenId : null;
    setDhTooltipTokenId(tooltipTokenId);
    safePostMessage({ type: 'dhTooltipTokenUpdate', tokenId: tooltipTokenId });
  }, [currentScene?.ttrpgSystem, safePostMessage]);

  const handleDhTooltipClose = useCallback(() => {
    setDhTooltipTokenId(null);
    safePostMessage({ type: 'dhTooltipTokenUpdate', tokenId: null });
  }, [safePostMessage]);

  // Update daggerheart token fields in-place (HP / Stress / extras)
  const handleDhTokenUpdate = useCallback((tokenId, partial) => {
    if (!currentScene) return;
    const updatedScene = {
      ...currentScene,
      tokens: currentScene.tokens.map((t) =>
        t.id === tokenId ? { ...t, ...partial } : t
      ),
    };
    persistScene(updatedScene);
  }, [currentScene, persistScene]);

  useEffect(() => {
    const panel = managerRef.current;
    if (!panel) return undefined;
    let measureFrame = null;
    const scheduleMeasure = () => {
      if (measureFrame !== null) cancelAnimationFrame(measureFrame);
      measureFrame = requestAnimationFrame(() => {
        measureFrame = null;
        if (!managerRef.current) return;
        const currentPanel = managerRef.current;
        const styles = window.getComputedStyle(currentPanel);
        const nextSize = {
          width: currentPanel.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight),
          height: currentPanel.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom),
        };
        setManagerViewportSize((previousSize) => (
          previousSize.width === nextSize.width && previousSize.height === nextSize.height
            ? previousSize
            : nextSize
        ));
      });
    };
    scheduleMeasure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleMeasure);
      return () => {
        window.removeEventListener('resize', scheduleMeasure);
        if (measureFrame !== null) cancelAnimationFrame(measureFrame);
      };
    }
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(panel);
    return () => {
      observer.disconnect();
      if (measureFrame !== null) cancelAnimationFrame(measureFrame);
    };
  }, [currentScene]);

  return (
    <div
      className={`page_container page_container${darkModeSuffix} orchestrator_container`}
      style={{ background: mode === 'dark' ? 'var(--jet)' : 'var(--ivory)' }}
    >
      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />
      <div className="page_top">
        <div className={`page_top page_top${darkModeSuffix}`}>
          <TopBar
            mode={mode}
            setMode={setMode}
            scenes={scenes}
            currentSceneId={currentSceneId}
            onSelectScene={setCurrentSceneId}
            onAddScene={openSceneModal}
            onEditScenes={() => setShowEditScenesModal(true)}
            isOnBreak={isOnBreak}
            onToggleBreak={toggleBreak}
            onAssetManagement={handleAssetManagement}
            onDisplayAction={openOrRefreshDisplay}
            managedDisplayIsOpen={isDisplayOpen}
          />
        </div>
      </div>
      <div className={`page_content page_content${darkModeSuffix} orchestrator_content`}>
        {!currentScene && <WelcomeTutorial mode={mode} />}
        {currentScene && (
          <div
            className={`orchestrator_split orchestrator_split${darkModeSuffix}`}
          >
            <ManagerSidebar
              sessionToken={sessionToken}
              ttrpgSystemId={currentScene?.ttrpgSystem || null}
              onSystemButtonClick={handleSystemButtonClick}
              onGridConfig={() => setShowGridConfigModal(true)}
              onShowDisplay={openOrRefreshDisplay}
              isDisplayOpen={isDisplayOpen}
              onAddToken={openAddToken}
              onAddBattleEffect={openAddBattleEffect}
              onAddLayer={openAddLayer}
              onAddEffect={openEffectPicker}
              onShowDiceHistory={() => setShowDiceHistoryModal(true)}
              onShowGmDice={() => setShowGmDiceModal(true)}
              onDeleteScene={() => askDeleteScene(currentSceneId)}
              canDeleteScene={!!currentSceneId}
              tokens={currentScene.tokens}
              onToggleTokenVisible={toggleTokenVisible}
              onToggleTokenDeath={toggleTokenDeath}
              onToggleTokenDisposition={toggleTokenDisposition}
              onEditToken={openEditToken}
              onToggleGroupTokensVisible={toggleGroupTokensVisible}
              onReorderTokens={reorderTokens}
              battleEffects={currentScene.battleEffects || []}
              onToggleBattleEffectVisible={toggleBattleEffectVisible}
              onEditBattleEffect={openEditBattleEffect}
              onTogglePaintBattleEffect={togglePaintBattleEffect}
              onDeleteBattleEffect={deleteBattleEffect}
              onToggleGroupEffectsVisible={toggleGroupEffectsVisible}
              onReorderEffects={reorderEffects}
              paintingBattleEffectId={paintingBattleEffectId}
              layers={currentScene.layers}
              onToggleLayer={toggleLayerVisible}
              onEditLayer={openEditLayer}
              onToggleGroupLayersVisible={toggleGroupLayersVisible}
              onStartLayerFlip={startLayerFlip}
              activeFlipLayerId={layerFlipState ? layerFlipState.layerIds[layerFlipState.activeIndex] : null}
              onReorderLayers={reorderLayers}
              drawingLayerId={drawingLayerId}
              onToggleDrawLayer={toggleDrawLayer}
              onImportScene={importSceneFromFile}
              onExportScene={exportSceneToFile}
              onLoadTokens={() => setShowLoadTokensModal(true)}
              showNamesMode={showNamesMode}
              onToggleNamesMode={handleToggleNamesMode}
              measuringMode={measuringMode}
              onToggleMeasuringMode={handleToggleMeasuringMode}
              measurementDisabled={gridConfig.type === 'hex'}
              hasSelectedToken={!!selectedTokenId}
              selectedTokenId={selectedTokenId}
              mode={mode}
              isZoomed={isZoomed}
              onToggleZoom={handleToggleZoom}
              autoCenterOnSelect={autoCenterOnSelect}
              onToggleAutoCenter={() => setAutoCenterOnSelect((prev) => !prev)}
              trackers={trackers}
              onAddTracker={openAddTracker}
              onEditTracker={openEditTracker}
              onDeleteTracker={handleDeleteTracker}
              onToggleTrackerVisible={handleToggleTrackerVisible}
              onChangeTrackerValue={handleChangeTrackerValue}
              onReorderTrackers={handleReorderTrackers}
              onToggleGroupTrackersVisible={toggleGroupTrackersVisible}
              isDisplayReversed={isDisplayReversed}
              onToggleDisplayReverse={toggleDisplayRotation}
              allowPlayerControls={allowPlayerControls}
              onTogglePlayerControls={togglePlayerControls}
              onShowWorldMap={() => setShowWorldMapModal(true)}
              onShowLibrary={() => setShowLibraryModal(true)}
              worldMapShared={worldMapShared}
              worldMapDisplaying={mapOverlayActive}
              libraryShared={libraryShared}
            />
            <div className="board_column">
                <div className="board_row">
                  <div
                    className={`manager_panel${mode === 'dark' ? ' manager_panel_dark' : ''}${isZoomed ? ' manager_panel_gm_zoomed' : ''}`}
                    ref={managerRef}
                  >
                    <GridBoard
                      ref={managerBoardRef}
                      scene={currentScene}
                      onDragToken={onDragToken}
                      onTokenDoubleClick={handleTokenRangeToggle}
                      rangeSourceId={rangeSourceId}
                      onNudgeToken={onNudgeToken}
                      selectedTokenId={selectedTokenId}
                      selectedLayerId={layerFlipState ? layerFlipState.layerIds[layerFlipState.activeIndex] : null}
                      onSelectToken={handleSelectToken}
                      editingBattleEffectId={paintingBattleEffectId}
                      onPaintBattleEffectCells={paintBattleEffectCells}
                      pings={pings}
                      onPingGrid={handlePingGrid}
                      showNames={showNamesMode}
                      gridCols={gridConfig.cols}
                      gridRows={gridConfig.rows}
                      gridType={gridConfig.type}
                      boardDimensions={managerViewportSize}
                      dragVisualScale={gmViewState.zoom}
                      boardOverlayScale={gmViewState.zoom}
                      pingZoomScale={gmViewState.zoom}
                      viewState={gmViewState}
                      fitMode={isZoomed && isDisplayOpen ? 'cover' : 'contain'}
                      onViewStateChange={updateGridViewState}
                      cameraInteractive={isZoomed}
                      viewportPreviewStore={isDisplayOpen ? displayViewportStore : null}
                      dhTooltipTokenId={dhTooltipTokenId}
                      onDhTokenUpdate={handleDhTokenUpdate}
                      onDhTooltipClose={handleDhTooltipClose}
                      drawingLayerId={drawingLayerId}
                      onDrawLayerStroke={addStrokeToDrawLayer}
                      onMoveTextLayer={moveTextLayer}
                    />
                    {isZoomed && (
                      <div className="manager_zoom_navigator" aria-label="Display preview">
                        <GridBoard
                          scene={currentScene}
                          readOnly
                          showNames={false}
                          pings={pings}
                          onPingDoubleClick={handlePingGrid}
                          gridCols={gridConfig.cols}
                          gridRows={gridConfig.rows}
                          gridType={gridConfig.type}
                          viewportPreviewStore={displayViewportStore}
                          keepDisplayViewportPreview
                          displayViewportInteractive
                          onMoveDisplayViewport={moveDisplayViewport}
                        />
                        <div className="manager_zoom_navigator_controls">
                          <ZoomRepeatButton
                            icon={ZoomOut}
                            description="Zoom out to the previous preset"
                            descriptionUp
                            disabled={displayZoom <= 1}
                            onPress={() => selectDisplayZoomPreset(-1)}
                            onRepeat={() => stepDisplayZoom(-1)}
                            repeatDelay={200}
                            repeatInterval={16}
                          />
                          <output>{Math.round(displayZoom * 100)}%</output>
                          <ZoomRepeatButton
                            icon={ZoomIn}
                            description="Zoom in to the next preset"
                            descriptionUp
                            disabled={displayZoom >= 5}
                            onPress={() => selectDisplayZoomPreset(1)}
                            onRepeat={() => stepDisplayZoom(1)}
                            repeatDelay={200}
                            repeatInterval={16}
                          />
                          <Button icon={RotateCcw} description="Reset Zoom" descriptionUp onPress={resetDisplayZoom} />
                        </div>
                      </div>
                    )}
                    {/* Arrow buttons removed from manager UI; keyboard nudging and token selection remain */}
                  </div>
                  {isDisplayOpen && (
                    <div className="scrollbar_panel">
                      <ViewportIndicator
                        boardRef={managerRef}
                        viewportStore={displayViewportStore}
                        channelRef={channelRef}
                        onMove={moveDisplayViewport}
                        inline
                        mode={mode}
                        cinematicModeActive={cinematicModeActive}
                        onToggleCinematicMode={() => setEffect(cinematicModeActive ? null : 'cinematicMode')}
                      />
                    </div>
                  )}
                </div>
              </div>
          </div>
        )}
      </div>

      {showSceneModal && (
        <SceneModal
          visible={showSceneModal}
          form={sceneForm}
          mode={mode}
          onChange={updateSceneForm}
          onCancel={() => setShowSceneModal(false)}
          onSave={saveSceneFromModal}
        />
      )}
      {showDiceHistoryModal && (
        <DiceHistoryModal
          visible={showDiceHistoryModal}
          rolls={sessionRolls}
          mode={mode}
          onClose={() => setShowDiceHistoryModal(false)}
        />
      )}
      {showGmDiceModal && (
        <GmDiceModal
          visible={showGmDiceModal}
          mode={mode}
          onRoll={handleGmRoll}
          onClose={() => setShowGmDiceModal(false)}
        />
      )}
      {showDeleteSceneModal && (
        <DeleteSceneModal
          visible={showDeleteSceneModal}
          scene={pendingDeleteScene}
          gridConfig={pendingDeleteScene?.gridConfig || gridConfig}
          mode={mode}
          onCancel={cancelDeleteScene}
          onConfirm={confirmDeleteScene}
          onExport={() => exportSceneToFile(pendingDeleteScene)}
        />
      )}

      {showBattleEffectModal && (
        <BattleEffectModal
          visible={showBattleEffectModal}
          mode={mode}
          form={battleEffectForm}
          onChange={updateBattleEffectForm}
          onCancel={() => setShowBattleEffectModal(false)}
          onSave={saveBattleEffectFromModal}
        />
      )}

      {showTokenModal && (
        <TokenModal
          visible={showTokenModal}
          form={tokenForm}
          isEditing={isEditingToken}
          mode={mode}
          system={currentScene?.ttrpgSystem || null}
          onChange={updateTokenForm}
          onCancel={() => setShowTokenModal(false)}
          onSave={saveTokenFromModal}
          onDelete={deleteTokenFromModal}
          gridType={gridConfig.type}
          onDuplicate={duplicateTokenFromModal}
        />
      )}

      {showLayerModal && (
        <LayerModal
          visible={showLayerModal}
          form={layerForm}
          isEditing={isEditingLayer}
          mode={mode}
          gridConfig={gridConfig}
          onChange={updateLayerForm}
          onCancel={() => setShowLayerModal(false)}
          onSave={saveLayerFromModal}
          onMassAdd={addMassImagesFromModal}
          onDelete={deleteLayerFromModal}
        />
      )}

      {showEffectModal && (
        <EffectModal
          visible={showEffectModal}
          mode={mode}
          onCancel={() => setShowEffectModal(false)}
          cinematicModeActive={cinematicModeActive}
          onSelect={(type) => {
            setEffect(type);
            setShowEffectModal(false);
          }}
          onClear={() => {
            setEffect(null);
            setShowEffectModal(false);
          }}
        />
      )}

      {isPreparingSession && <PreparingScreen topOffset={80} />}
      {isOnBreak && <BreakScreen topOffset={80} />}

      {showLoadTokensModal && (
        <LoadTokensModal
          visible={showLoadTokensModal}
          scenes={(scenes || []).filter((s) => s.id !== currentSceneId)}
          currentTtrpgSystem={currentScene?.ttrpgSystem || null}
          mode={mode}
          onCancel={() => setShowLoadTokensModal(false)}
          onImport={importTokensFromScenes}
        />
      )}

      {showSystemSelectModal && (
        <SystemSelectModal
          visible={showSystemSelectModal}
          mode={mode}
          onClose={() => setShowSystemSelectModal(false)}
          onSelect={(systemId) => {
            handleSelectSystem(systemId);
            setShowSystemSelectModal(false);
          }}
        />
      )}

      {showSystemDmScreenModal && currentScene?.ttrpgSystem && (
        <SystemDmScreenModal
          visible={showSystemDmScreenModal}
          systemId={currentScene.ttrpgSystem}
          mode={mode}
          onClose={() => setShowSystemDmScreenModal(false)}
          onChangeSystem={() => setShowSystemSelectModal(true)}
        />
      )}

      {showTrackerModal && (
        <TrackerModal
          visible={showTrackerModal}
          form={trackerForm}
          isEditing={isEditingTracker}
          mode={mode}
          onChange={(updates) => setTrackerForm((prev) => ({ ...prev, ...updates }))}
          onCancel={() => setShowTrackerModal(false)}
          onSave={saveTrackerFromModal}
          onDelete={() => {
            handleDeleteTracker(trackerForm.id);
            setShowTrackerModal(false);
          }}
        />
      )}

      {showEditScenesModal && (
        <EditScenesModal
          visible={showEditScenesModal}
          scenes={scenes}
          mode={mode}
          onCancel={() => setShowEditScenesModal(false)}
          onSave={handleSaveEditedScenes}
          onDeleteScene={(sceneId) => {
            setShowEditScenesModal(false);
            askDeleteScene(sceneId);
          }}
        />
      )}

      {showWorldMapModal && (
        <WorldMapModal
          visible={showWorldMapModal}
          worldMap={worldMap || { layers: [] }}
          isGm={true}
          mode={mode}
          onClose={() => setShowWorldMapModal(false)}
          onSave={handleSaveWorldMap}
          playerShareActive={worldMapShared}
          onTogglePlayerShare={handleToggleWorldMapShare}
          displayOverlayActive={mapOverlayActive}
          onToggleDisplayOverlay={handleToggleDisplayOverlay}
          onViewChange={handleMapViewChange}
          initialView={mapOverlayViewRef.current}
        />
      )}

      {showLibraryModal && (
        <LibraryModal
          visible={showLibraryModal}
          library={library || { books: [] }}
          isGm={true}
          mode={mode}
          onClose={() => setShowLibraryModal(false)}
          onSave={handleSaveLibrary}
          playerShareActive={libraryShared}
          onTogglePlayerShare={handleToggleLibraryShare}
        />
      )}

      {showAssetManagerModal && (
        <AssetManagerModal
          visible={showAssetManagerModal}
          mode={mode}
          scenes={scenes}
          onClose={() => setShowAssetManagerModal(false)}
          isGameplaySyncEnabled={isGameplaySyncEnabled}
          onEnableGameplaySync={handleEnableGameplaySync}
          onDisableGameplaySync={handleDisableGameplaySync}
          isSyncing={isSyncingAssets}
        />
      )}

      {showCloudAccessModal && (
        <CloudAccessModal
          visible={showCloudAccessModal}
          mode={mode}
          onClose={() => setShowCloudAccessModal(false)}
        />
      )}

      {showGameplaySyncErrorModal && (
        <GameplaySyncErrorModal
          visible={showGameplaySyncErrorModal}
          mode={mode}
          onClose={() => setShowGameplaySyncErrorModal(false)}
          onStoreToCloud={handleStoreLocalToCloud}
          onLoadFromCloud={handleLoadFromCloudAndResolve}
          onDisableSync={handleDisableSyncFromConflict}
        />
      )}

      <GridConfigModal
        visible={showGridConfigModal}
        gridConfig={gridConfig}
        mode={mode}
        onCancel={() => setShowGridConfigModal(false)}
        onSave={handleGridConfigSave}
      />
    </div>
  );
}

export default OrchestratorManager;
